// Fresco — polished pan-zoom image viewer for Phoenix apps.
//
// Hand-rolled CSS-transform pan/zoom engine. Zero external JS deps; no
// canvas, no tile pyramids, no spring math, no CDN load. The single <img>
// (or N <img>s for <Fresco.canvas>) lives inside a stage div; the engine
// translates the stage and sizes each img per-frame. Native Pointer Events
// drive gestures; native Fullscreen API handles fullscreen.
//
// Public surface (unchanged from 0.4.x where compatible):
//
//   window.Fresco.viewerFor(domId)             // → viewer handle, or null
//   window.Fresco.onViewerReady(domId, cb)     // fires once when ready
//   window.Fresco.onReady(domId, cb)           // alias of onViewerReady
//   window.Fresco.registerSourceProvider(predicate, factory)
//
// Viewer handle (returned by viewerFor):
//
//   { container,
//     imageToScreen(pt), screenToImage(pt),
//     getViewportBounds(),
//     fitBounds(rect, immediately),
//     setSource(url), swapSourcePreservingBounds(url),
//     on(eventName, handler) → unsubscribe,
//     appendNavButton(svg, title, onClick) → unsubscribe (+ .setIcon/.setTitle/.el) }
//
// Canvas handle (additionally):
//
//   { getCanvasSize(), getImages(), imageBoundsFor(id), fitImage(id),
//     getExtension(name),
//     setSources(sources, opts) → Promise,  // replace the image set in place
//     setImageSrc(id, url),                  // swap one image's src (load-window paging)
//     whenLayoutSettled() → Promise }        // resolves after the next frame paints
//
// Events fired through `handle.on(eventName, fn)`:
//   "zoom" / "pan" / "open" / "resize"           — fired on intent
//   "animation" / "update-viewport"              — fired per-frame
//   "image-loaded"                               — canvas only; per-image load events
//   "sources-changed"                            — canvas only; setSources completed
//
// Notes vs. 0.4.x:
//   - `handle.openSeadragon` / `handle.viewer` are gone. The engine is no
//     longer OSD-backed; there's no underlying instance to escape to.
//   - `getViewportBounds()` returns image-pixel (viewer) or canvas-pixel
//     (canvas) coords `{x, y, width, height}`, not OSD-normalized 0–1 rects.
//   - `fitBounds(..., immediately)` ignores `immediately` — the lite engine
//     has no animation system in 0.5.x.
//
// Parent app wiring:
//   import "../../deps/fresco/priv/static/fresco.js"
//   hooks: { ...window.FrescoHooks, ...colocatedHooks }

(function() {
  if (window.FrescoLoaded) return;
  window.FrescoLoaded = true;

  // ===========================================================================
  // Extension surface — same shape as 0.4.x so consumers (Tessera, future
  // Etcher) attach the same way. The default `{type: "image", url}` provider
  // matches plain image URLs; Tessera-lite will register a `{type: "tiles", …}`
  // factory when it lands. The viewer engine dispatches on `resolved.type`
  // — it currently only knows "image" and throws a clear error for anything
  // else so future tile-source integration fails loudly rather than silently.
  // ===========================================================================

  // Handle registry + ready-callback queue live on the shared
  // `window.Fresco` global so peer packages (currently `fresco_strip`,
  // future ones too) can register handles into the same map. Both
  // packages defensively idempotent-init the registry; whichever
  // package loads first creates it, the other piggy-backs. Without
  // this, fresco's closure-local var and fresco_strip's
  // window-scoped var would diverge and `onViewerReady("strip-id")`
  // calls would queue forever (fresco's queue) while the handle sat
  // on the other map (fresco_strip's). Fixed in 0.6.1 — 0.6.0
  // shipped with the two registries un-shared by accident.
  window.Fresco = window.Fresco || {};
  window.Fresco.viewerRegistry  = window.Fresco.viewerRegistry  || {};
  window.Fresco._readyCallbacks = window.Fresco._readyCallbacks || {};
  var viewerRegistry  = window.Fresco.viewerRegistry;
  var readyCallbacks  = window.Fresco._readyCallbacks;
  var sourceProviders = [];       // [{predicate, factory}]

  sourceProviders.push({
    predicate: function() { return true; },
    factory: function(url) { return { type: "image", url: url }; }
  });

  function resolveTileSource(url) {
    for (var i = 0; i < sourceProviders.length; i++) {
      if (sourceProviders[i].predicate(url)) {
        return sourceProviders[i].factory(url);
      }
    }
    return { type: "image", url: url };
  }

  // `Object.assign` (not `window.Fresco = {...}`) so the public API
  // surface cooperates with whichever package loaded first. A bare
  // assignment would clobber `fresco_strip`'s defensive setup;
  // assign-onto preserves it. Methods themselves are idempotent —
  // re-installing them at module-load time is harmless.
  Object.assign(window.Fresco, {
    viewerFor: function(domId) {
      return viewerRegistry[domId] || null;
    },

    scrollStripFor: function(domId) {
      return viewerRegistry[domId] || null;
    },

    onViewerReady: function(domId, callback) {
      var handle = viewerRegistry[domId];
      if (handle) { callback(handle); return; }
      readyCallbacks[domId] = readyCallbacks[domId] || [];
      readyCallbacks[domId].push(callback);
    },

    onReady: function(domId, callback) {
      return window.Fresco.onViewerReady(domId, callback);
    },

    registerSourceProvider: function(predicate, factory) {
      sourceProviders.unshift({ predicate: predicate, factory: factory });
    },

    // The Apple Photos way of going through pictures — see `createGallery`.
    gallery: function(opts) {
      return createGallery(opts);
    }
  });


  // ===========================================================================
  // Gallery — step through pictures the way Apple Photos does.
  //
  // A strip of pictures (the one being looked at and its neighbours, already
  // decoded) laid over a host's viewer. A finger, a mouse or a sideways
  // trackpad scroll slides the strip and the next picture comes in beside the
  // current one; let go nearly halfway across, or flick, and it settles on
  // the next one, otherwise it springs back. Nothing waits on anyone: the host
  // hears about the new picture once the strip has settled (`onChange`), and
  // the strip keeps showing it until the host says its own viewer has painted
  // that picture (`release`), when it hands over in place.
  //
  // Fresco does not know what the pictures are or where they come from. The
  // host gives it a window of items — `{key, src, width, height, rotation,
  // actual, kind, label}` — and says where to sit (`getRect`, inside
  // `container` when given, else body), where a
  // gesture may start (`getTarget`), when one may (`canStart`: at the
  // opening zoom, not mid-annotation), and where its current picture is on
  // screen (`getCurrentRect`) so the strip starts exactly over it.
  //
  //   var g = Fresco.gallery({getRect, getTarget, canStart, getCurrentRect,
  //                           background, onChange, onShow, onHide});
  //   g.setItems(items, index);  g.step(+1);  g.release(key, rect);
  //   g.destroy();
  // ===========================================================================

  var GALLERY_GAP = 28;             // px between neighbouring pictures
  var GALLERY_COMMIT = 0.45;        // of the width, to settle on a neighbour when let go slowly
  var GALLERY_FLICK = 0.35;         // px/ms — a quick flick settles too
  var GALLERY_WHEEL_COMMIT = 0.25;  // a trackpad scroll needs less travel
  var GALLERY_RESIST = 0.32;        // past the first / last picture

  var GALLERY_SPRING_W = 0.0165;   // 1/ms: a ~380ms response, as Photos pages
  var GALLERY_MAX_FLING = 4;       // px/ms: past this a flick adds nothing

  // A critically damped spring from displacement x0 (px from the target) at
  // velocity v0 (px/ms), as {ms, ease}: the time to settle within half a
  // pixel, and a CSS `linear()` easing tracing it. Browsers without
  // `linear()` get the nearest cubic-bezier. Null when already there.
  function springEasing(x0, v0) {
    if (Math.abs(x0) < 0.5) return null;
    // Softer over a long way, so what is left of the width after a slow
    // release is covered at an even pace (a peak of ~3 px/ms) rather than
    // in a rush.
    var w = Math.min(GALLERY_SPRING_W, Math.max(0.011, Math.E * 3 / Math.abs(x0)));
    v0 = Math.max(-GALLERY_MAX_FLING, Math.min(GALLERY_MAX_FLING, v0));
    var pos = function(t) { return (x0 + (v0 + w * x0) * t) * Math.exp(-w * t); };
    var ms = 120;
    // Done within a pixel: a spring's last pixel creeps on for a long time,
    // and everything after the glide (the server, the hand-over) waits on it.
    while (ms < 900 && (Math.abs(pos(ms)) > 1 || Math.abs(pos(ms + 16) - pos(ms)) > 0.5)) ms += 8;
    if (!springLinearSupported()) {
      return { ms: Math.round(ms * 0.8), ease: "cubic-bezier(0.25, 0.9, 0.3, 1)" };
    }
    var pts = [];
    var n = Math.max(16, Math.min(48, Math.round(ms / 16)));
    for (var i = 0; i <= n; i++) {
      var p = i === n ? 1 : 1 - pos(ms * i / n) / x0;
      pts.push(+p.toFixed(4));
    }
    return { ms: Math.round(ms), ease: "linear(" + pts.join(", ") + ")" };
  }

  var springLinear = null;
  function springLinearSupported() {
    if (springLinear === null) {
      springLinear = !!(window.CSS && CSS.supports &&
        CSS.supports("transition-timing-function", "linear(0, 0.5, 1)"));
    }
    return springLinear;
  }

  function createGallery(opts) {
    opts = opts || {};
    var items = [];
    var index = 0;
    var root = null, track = null;
    var slides = {};       // key -> { el, img, box }
    var boxes = {};        // key -> the box its picture was given on screen
    var cache = {};        // src -> decoded Image, kept so it stays decoded
    var offset = 0;        // px, how far the strip is pulled
    var W = 0, H = 0, rect = null;
    var shown = false;     // the strip is on screen
    var holding = false;   // showing a picture the host has not painted yet
    var animating = null;  // { done, timer } while settling
    var drag = null;
    var pointers = {};
    var wheel = null;
    var wheelQuietUntil = 0;
    var fadeTimer = null;  // the hand-over fade, until it hides the strip
    var swipedAt = 0;      // when a swipe last let go
    var dead = false;

    function now() { return (window.performance && performance.now) ? performance.now() : Date.now(); }
    function dpr() { return window.devicePixelRatio || 1; }
    function cur() { return items[index] || null; }

    function ensureRoot() {
      if (root) return;
      root = document.createElement("div");
      root.className = "fresco-gallery";
      root.setAttribute("aria-hidden", "true");
      root.style.cssText = "position:fixed;left:0;top:0;width:0;height:0;overflow:hidden;" +
        "pointer-events:none;display:none;z-index:" + (opts.zIndex || 1001) + ";";
      track = document.createElement("div");
      track.style.cssText = "position:absolute;left:0;top:0;width:100%;height:100%;will-change:transform;";
      root.appendChild(track);
      // Inside the host's modal when it gives one, so a top-layer or
      // stacking-context modal cannot cover the strip.
      (opts.container || document.body).appendChild(root);
    }

    function place() {
      rect = opts.getRect ? opts.getRect() : null;
      if (!rect || !rect.width || !rect.height) return false;
      W = rect.width; H = rect.height;
      root.style.left = rect.left + "px";
      root.style.top = rect.top + "px";
      root.style.width = W + "px";
      root.style.height = H + "px";
      root.style.background = opts.background ? (typeof opts.background === "function" ? opts.background() : opts.background) : "#000";
      return true;
    }

    // Where a picture goes on screen: its own box (unturned), centred, at the
    // scale the viewer opens it at — fit, or 100% for a small one when the
    // host's viewer uses actual size.
    function boxFor(item) {
      if (boxes[item.key]) return boxes[item.key];
      var rot = (((item.rotation || 0) % 360) + 360) % 360;
      var quarter = rot === 90 || rot === 270;
      var dec = cache[item.src];
      var nw = item.width || (dec && dec.naturalWidth) || 0;
      var nh = item.height || (dec && dec.naturalHeight) || 0;
      if (!(nw > 0 && nh > 0)) return null;
      var bw = quarter ? nh : nw, bh = quarter ? nw : nh;
      var sc = Math.min(W / bw, H / bh);
      if (item.actual) sc = Math.min(sc, 1 / dpr());
      return { cx: W / 2, cy: H / 2, w: nw * sc, h: nh * sc, rotation: rot };
    }

    // Property by property rather than one cssText, so a transition set on
    // the element carries a change of box (the hand-over glide).
    function styleImg(img, box) {
      var st = img.style;
      st.position = "absolute";
      st.userSelect = "none";
      st.transformOrigin = "50% 50%";
      if (!box) {
        st.left = "0"; st.top = "0"; st.width = "100%"; st.height = "100%";
        st.objectFit = "contain"; st.transform = "";
        return;
      }
      st.objectFit = "fill";
      st.left = (box.cx - box.w / 2) + "px";
      st.top = (box.cy - box.h / 2) + "px";
      st.width = box.w + "px";
      st.height = box.h + "px";
      st.transform = "rotate(" + (box.rotation || 0) + "deg)";
    }

    function makeSlide(item) {
      var el = document.createElement("div");
      el.style.cssText = "position:absolute;left:0;top:0;width:100%;height:100%;";
      var img = null;
      if (item.kind && item.kind !== "image") {
        var card = document.createElement("div");
        card.style.cssText = "position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);" +
          "color:rgba(255,255,255,0.85);font:500 14px/1.4 ui-sans-serif,system-ui,sans-serif;" +
          "text-align:center;padding:24px 28px;border-radius:14px;background:rgba(255,255,255,0.08);max-width:70%;";
        card.textContent = item.label || "";
        el.appendChild(card);
      } else {
        // The preloaded, already decoded image itself when it is free, so
        // the first frame of a swipe has nothing left to decode.
        var pre = cache[item.src];
        img = pre && !pre.parentNode ? pre : document.createElement("img");
        img.alt = "";
        img.draggable = false;
        if (img !== pre) { img.decoding = "async"; img.src = item.src; }
        el.appendChild(img);
        var box = boxFor(item);
        styleImg(img, box);
        if (!box && img.complete && img.naturalWidth) {
          box = boxFor(item);
          styleImg(img, box);
        }
        if (!box) {
          // No size known yet: lay it out once it has one.
          img.addEventListener("load", function() {
            var b = boxFor(item);
            if (b) styleImg(img, b);
          }, { once: true });
        }
      }
      return { el: el, img: img, item: item };
    }

    // Lay the neighbours out beside the current picture: -1, 0, +1.
    function layout() {
      var want = {};
      for (var k = -1; k <= 1; k++) {
        var item = items[index + k];
        if (!item) continue;
        want[item.key] = k;
        var sl = slides[item.key];
        if (!sl) {
          sl = slides[item.key] = makeSlide(item);
          track.appendChild(sl.el);
        }
        sl.el.style.transform = "translate3d(" + (k * (W + GAP())) + "px,0,0)";
      }
      Object.keys(slides).forEach(function(key) {
        if (!(key in want)) {
          if (slides[key].el.parentNode) slides[key].el.parentNode.removeChild(slides[key].el);
          delete slides[key];
        }
      });
      paint();
    }

    function GAP() { return GALLERY_GAP; }

    // One paint a frame however many moves arrive in it.
    var paintFrame = 0;
    function paintSoon() {
      if (paintFrame) return;
      paintFrame = requestAnimationFrame(function() { paintFrame = 0; if (!animating) paint(); });
    }

    function paint(transition) {
      if (paintFrame) { cancelAnimationFrame(paintFrame); paintFrame = 0; }
      track.style.transition = transition || "none";
      track.style.transform = "translate3d(" + offset + "px,0,0)";
    }

    // Keep the pictures around the current one fetched and decoded, so a
    // swipe never shows an empty slot: two either side, the rest let go.
    function preload() {
      var keep = {};
      for (var k = -2; k <= 2; k++) {
        var item = items[index + k];
        if (!item || !item.src || (item.kind && item.kind !== "image")) continue;
        keep[item.src] = true;
        if (cache[item.src]) continue;
        var im = new Image();
        im.decoding = "async";
        im.src = item.src;
        cache[item.src] = im;
        if (im.decode) im.decode().catch(function() {});
      }
      Object.keys(cache).forEach(function(src) { if (!keep[src]) delete cache[src]; });
    }

    function show() {
      ensureRoot();
      if (!place()) return false;
      if (!shown) {
        // The picture on screen right now, exactly where the viewer has it,
        // so taking over from the viewer is invisible.
        var c = cur();
        if (c && opts.getCurrentRect) {
          var r = opts.getCurrentRect();
          if (r && r.w > 0 && r.h > 0) {
            boxes[c.key] = { cx: r.cx - rect.left, cy: r.cy - rect.top, w: r.w, h: r.h, rotation: r.rotation || 0 };
          }
        }
        root.style.display = "block";
        shown = true;
        if (opts.onShow) opts.onShow();
      }
      // A gesture during the hand-over fade takes the strip back, opaque.
      clearTimeout(fadeTimer);
      fadeTimer = null;
      root.style.transition = "none";
      root.style.opacity = "1";
      layout();
      return true;
    }

    function hide() {
      if (!root) return;
      clearTimeout(fadeTimer);
      fadeTimer = null;
      root.style.display = "none";
      shown = false;
      holding = false;
      offset = 0;
      Object.keys(slides).forEach(function(key) {
        if (slides[key].el.parentNode) slides[key].el.parentNode.removeChild(slides[key].el);
      });
      slides = {};
      boxes = {};
      if (opts.onHide) opts.onHide();
    }

    function hasNeighbour(dir) { return !!items[index + dir]; }

    // Past the first or last picture the strip still moves, but grudgingly.
    function resist(dx) {
      if ((dx > 0 && !hasNeighbour(-1)) || (dx < 0 && !hasNeighbour(1))) return dx * GALLERY_RESIST;
      return dx;
    }

    // Never further than one picture: a long drag or a trackpad's momentum
    // would otherwise run past the neighbour and snap back to it.
    function pull(base, dx) {
      var span = W + GAP();
      return Math.max(-span, Math.min(span, base + resist(dx)));
    }

    function finishAnimation() {
      if (!animating) return;
      var a = animating;
      animating = null;
      clearTimeout(a.timer);
      a.done();
    }

    // Glide to `target` on a critically damped spring — the motion Apple
    // Photos uses. It starts at the finger's speed (`velocity`, px/ms; 0 for
    // a key press) and is pulled into place with no burst and no bounce, so
    // letting go reads as the picture carrying on rather than jumping. The
    // spring is sampled into a CSS `linear()` easing, so the glide is still a
    // transition and runs on the compositor even while the page is busy.
    function animateTo(target, velocity, done) {
      var x0 = offset - target;
      // A key press has no finger speed to carry on: a plain ease, brisk.
      var spring = velocity == null
        ? (Math.abs(x0) < 0.5 ? null : { ms: 380, ease: "cubic-bezier(0.32, 0.08, 0.24, 1)" })
        : springEasing(x0, velocity);
      offset = target;
      if (!spring) { paint(); done(); return; }
      paint("transform " + spring.ms + "ms " + spring.ease);
      animating = { done: done, timer: setTimeout(finishAnimation, spring.ms + 30) };
    }

    // Catch the strip mid-glide, where it is on screen right now, the way a
    // finger catches a sliding photo — so a swipe never waits for the last
    // one to finish. A glide that was going to a neighbour has arrived as
    // far as counting goes (the picture is current, the host is told); only
    // the drawing stays where the finger caught it.
    function catchAnimation() {
      if (!animating) return;
      var page = animating.page || 0;
      clearTimeout(animating.timer);
      animating = null;
      var x = offset;
      try { x = new DOMMatrix(window.getComputedStyle(track).transform).m41; } catch (_e) {}
      offset = x;
      if (page) {
        index += page;
        offset += page * (W + GAP());
        holding = true;
        layout();
        preload();
        if (opts.onChange) opts.onChange(index, cur());
      } else {
        paint();
      }
    }

    // Let go: a flick goes one picture on in its direction — from the
    // picture the strip counts itself on, so quick flicks in a row each go
    // one further, even caught mid-glide. Let go slowly, it settles on the
    // nearest picture (past `GALLERY_COMMIT` of the way counts as nearer).
    function settle(velocity, wheelGesture) {
      var span = W + GAP();
      var pos = -offset / span;  // in pictures: +1 is the next one, centred
      var commitAt = wheelGesture ? GALLERY_WHEEL_COMMIT : GALLERY_COMMIT;
      var page;
      if (Math.abs(velocity) > GALLERY_FLICK) {
        page = velocity < 0 ? 1 : -1;
      } else {
        page = Math.abs(pos) > commitAt ? (pos > 0 ? 1 : -1) : 0;
      }
      if (page && !hasNeighbour(page)) page = 0;
      glideTo(page, velocity);
    }

    // Glide so that picture `page` (-1, 0, +1 from the current) is centred.
    function glideTo(page, velocity) {
      var done = page
        ? function() { land(page); }
        : function() { if (!holding) hide(); };
      animateTo(-page * (W + GAP()), velocity, done);
      if (animating) animating.page = page;
    }

    function land(dir) {
      index += dir;
      offset = 0;
      holding = true;
      layout();
      preload();
      if (opts.onChange) opts.onChange(index, cur());
    }

    // ── pointer ──────────────────────────────────────────────────────────
    function inTarget(e) {
      var t = opts.getTarget ? opts.getTarget() : null;
      return !!(t && e.target && t.contains(e.target));
    }

    function onDown(e) {
      if (dead) return;
      // A first finger (or any mouse press) starts afresh: a pointerup that
      // never reached us (let go outside the window) must not make this
      // look like the second finger of a pinch.
      if (e.isPrimary) pointers = {};
      pointers[e.pointerId] = true;
      var count = Object.keys(pointers).length;
      if (count > 1) {
        // A second finger is a pinch: the viewer's, not ours.
        if (drag && drag.engaged) { drag = null; settle(0, false); }
        drag = null;
        return;
      }
      if (e.button !== 0 || !inTarget(e)) return;
      if (opts.canStart && !opts.canStart(e)) return;
      drag = { id: e.pointerId, x0: e.clientX, y0: e.clientY, engaged: false, samples: [] };
    }

    function sample(d, x) {
      var t = now();
      d.samples.push({ x: x, t: t });
      while (d.samples.length > 2 && t - d.samples[0].t > 90) d.samples.shift();
    }

    function velocityOf(d) {
      var sm = d.samples;
      if (sm.length < 2) return 0;
      var a = sm[0], b = sm[sm.length - 1];
      var dt = b.t - a.t;
      return dt > 0 ? (b.x - a.x) / dt : 0;
    }

    function onMove(e) {
      var d = drag;
      if (!d || e.pointerId !== d.id) return;
      var dx = e.clientX - d.x0, dy = e.clientY - d.y0;
      if (!d.engaged) {
        if (Math.abs(dx) < 8) return;
        if (Math.abs(dx) < Math.abs(dy) * 1.2) { drag = null; return; }
        catchAnimation();
        if (!show()) { drag = null; return; }
        d.engaged = true;
        d.base = offset;
        d.x0 = e.clientX;
        dx = 0;
      }
      if (e.cancelable) e.preventDefault();
      // The strip has the gesture now: the viewer underneath must not pan
      // along with it (its pointerup still reaches it, to end its own state).
      e.stopPropagation();
      sample(d, e.clientX);
      offset = pull(d.base, dx);
      paintSoon();
    }

    function onUp(e) {
      delete pointers[e.pointerId];
      var d = drag;
      if (!d || e.pointerId !== d.id) return;
      drag = null;
      if (!d.engaged) return;
      swipedAt = now();
      settle(e.type === "pointercancel" ? 0 : velocityOf(d), false);
    }

    // ── trackpad ─────────────────────────────────────────────────────────
    // A sideways two-finger scroll is the desktop swipe. The trackpad keeps
    // sending its momentum after the fingers lift; those are swallowed until
    // it goes quiet, so one flick is one picture.
    function onWheel(e) {
      if (dead || e.ctrlKey) return;
      var t = now();
      if (!wheel && t < wheelQuietUntil && inTarget(e)) {
        wheelQuietUntil = t + 180;
        if (e.cancelable) e.preventDefault();
        e.stopPropagation();
        return;
      }
      var ax = Math.abs(e.deltaX), ay = Math.abs(e.deltaY);
      if (!wheel) {
        if (!inTarget(e) || ax < 3 || ax < ay * 1.3) return;
        if (opts.canStart && !opts.canStart(e)) return;
        catchAnimation();
        if (!show()) return;
        wheel = { base: offset, acc: 0, samples: [], timer: null, peak: 0 };
      }
      if (e.cancelable) e.preventDefault();
      e.stopPropagation();
      var unit = e.deltaMode === 1 ? 16 : (e.deltaMode === 2 ? W : 1);
      wheel.acc -= e.deltaX * unit;
      sample(wheel, wheel.acc);
      offset = pull(wheel.base, wheel.acc);
      paint();
      clearTimeout(wheel.timer);
      // Settle as soon as the scroll has clearly decided — a full picture
      // pulled, or past the commit point and slowing (the fingers are off
      // and this is the trackpad's momentum) — rather than holding the strip
      // still until the momentum runs out. The rest is swallowed.
      wheel.peak = Math.max(wheel.peak, ax);
      var span = W + GAP();
      if (Math.abs(offset) >= span - 1 ||
          (Math.abs(offset) > GALLERY_WHEEL_COMMIT * W && ax < wheel.peak * 0.5)) {
        endWheel();
        return;
      }
      wheel.timer = setTimeout(endWheel, 110);
    }

    function endWheel() {
      var w = wheel;
      wheel = null;
      if (!w) return;
      wheelQuietUntil = now() + 180;
      settle(velocityOf(w), true);
    }

    // Browsers still fire click after a drag, so two quick swipes made a
    // double-click — and the viewer underneath zoomed in on it, which then
    // refused the next swipe. Neither is the person's: swallow them.
    function onClick(e) {
      if (dead) return;
      var t = now() - swipedAt;
      if ((e.type === "click" && t < 80) || (e.type === "dblclick" && t < 700)) {
        e.stopPropagation();
        e.preventDefault();
      }
    }

    document.addEventListener("click", onClick, true);
    document.addEventListener("dblclick", onClick, true);
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("pointermove", onMove, { capture: true, passive: false });
    document.addEventListener("pointerup", onUp, true);
    document.addEventListener("pointercancel", onUp, true);
    document.addEventListener("wheel", onWheel, { capture: true, passive: false });

    return {
      // The window of pictures around the current one. When the strip is
      // up the picture on screen is found again by key, so a host refreshing
      // its window mid-swipe never moves what is being looked at.
      setItems: function(list, idx) {
        var keep = shown && cur() ? cur().key : null;
        items = Array.isArray(list) ? list.slice() : [];
        var at = -1;
        if (keep) for (var i = 0; i < items.length; i++) if (items[i].key === keep) { at = i; break; }
        index = at >= 0 ? at : Math.max(0, Math.min(items.length - 1, idx | 0));
        if (shown) layout();
        preload();
      },
      // Step by one picture with the same slide — arrow keys, buttons.
      // False when there is no neighbour that way or the strip cannot show.
      step: function(dir) {
        dir = dir < 0 ? -1 : 1;
        if (dead || drag || wheel) return false;
        catchAnimation();
        if (!hasNeighbour(dir) || !show()) return false;
        animateTo(-dir * (W + GAP()), null, function() { land(dir); });
        if (animating) animating.page = dir;
        return true;
      },
      currentKey: function() { return cur() ? cur().key : null; },
      // In the middle of a gesture or a glide: a host can hold back heavy
      // work (re-rendering its viewer) until the strip is still.
      isBusy: function() { return !!((drag && drag.engaged) || wheel || animating); },
      isShown: function() { return shown; },
      // The host has painted `key`: hand over. `to` (screen rect of the
      // host's picture, {cx, cy, w, h}) lets the strip's picture settle onto
      // it first, so a slightly different fit never jumps.
      release: function(key, to) {
        if (!shown || drag || wheel || animating || !cur() || cur().key !== key) return false;
        var sl = slides[key];
        var fade = function() {
          if (!shown || drag || wheel || animating) return;
          root.style.transition = "opacity 180ms ease";
          root.style.opacity = "0";
          fadeTimer = setTimeout(function() {
            fadeTimer = null;
            if (!drag && !wheel && !animating && cur() && cur().key === key) hide();
            else { root.style.transition = "none"; root.style.opacity = "1"; }
          }, 190);
        };
        if (sl && sl.img && to && to.w > 0 && rect) {
          var box = { cx: to.cx - rect.left, cy: to.cy - rect.top, w: to.w, h: to.h, rotation: to.rotation || 0 };
          sl.img.style.transition = "left 160ms ease, top 160ms ease, width 160ms ease, height 160ms ease";
          void sl.img.offsetWidth; // the start box is committed before it moves
          styleImg(sl.img, box);
          setTimeout(function() { if (sl.img) sl.img.style.transition = ""; fade(); }, 170);
        } else {
          fade();
        }
        return true;
      },
      hide: hide,
      destroy: function() {
        dead = true;
        document.removeEventListener("click", onClick, true);
        document.removeEventListener("dblclick", onClick, true);
        document.removeEventListener("pointerdown", onDown, true);
        document.removeEventListener("pointermove", onMove, { capture: true, passive: false });
        document.removeEventListener("pointerup", onUp, true);
        document.removeEventListener("pointercancel", onUp, true);
        document.removeEventListener("wheel", onWheel, { capture: true, passive: false });
        finishAnimation();
        if (root && root.parentNode) root.parentNode.removeChild(root);
        root = null;
        cache = {};
      }
    };
  }

  function publishReady(domId, handle) {
    viewerRegistry[domId] = handle;
    var cbs = readyCallbacks[domId] || [];
    delete readyCallbacks[domId];
    cbs.forEach(function(cb) { cb(handle); });
  }

  function unpublish(domId) {
    delete viewerRegistry[domId];
  }

  // Opt-in server bridge (`data-persist-rotation="true"`): forward the
  // client-side `rotate` event to the LiveView/LiveComponent so a host can
  // persist the user's chosen rotation. This is the only pushEvent in
  // Fresco and is off by default — consumers who don't opt in pay nothing.
  // Payload: the host element id (which viewer/canvas) + the new angle,
  // with `previous` so a host can diff. Fires on every rotation change,
  // including the Reset-view button snapping back to home rotation.
  // Shared by the FrescoViewer and FrescoCanvas hooks; returns the bus
  // unsubscribe (or null when not opted in) for the hook's destroyed().
  function wireRotationBridge(hook) {
    if (hook.el.dataset.persistRotation !== "true") return null;
    return hook.handle.on("rotate", function(e) {
      var payload = { id: hook.el.id, rotation: e.rotation, previous: e.previous };
      // Route to the LiveComponent that owns the host element (via
      // `pushEventTo(el, …)`) when there is one, else the LiveView.
      // A bare `pushEvent` always targets the root LiveView, which breaks
      // when the host handles the event in the component that renders the
      // element (e.g. a media viewer LiveComponent). Mirrors Etcher's
      // `pushEventTo(this.el, …)` convention.
      if (hook.pushEventTo) {
        hook.pushEventTo(hook.el, "fresco:rotate", payload);
      } else if (hook.pushEvent) {
        hook.pushEvent("fresco:rotate", payload);
      }
    });
  }

  // `MouseEvent.button` for the middle button / scroll-wheel click.
  var MIDDLE_BUTTON = 1;

  // ===========================================================================
  // Heroicons (outline, 24×24, stroke="currentColor")
  // ===========================================================================

  var ICONS = {
    zoomIn:  '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607ZM10.5 7.5v6m3-3h-6"/></svg>',
    zoomOut: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607ZM13.5 10.5h-6"/></svg>',
    // House = "home view" (the OpenSeadragon convention). Deliberately NOT
    // another circular-arrows glyph — reset and rotate used to be two
    // near-identical loops that were indistinguishable at nav-button size.
    reset:   '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8"/><path stroke-linecap="round" stroke-linejoin="round" d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>',
    expand:  '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15"/></svg>',
    // Heroicons `arrow-path-rounded-square` — quarter-turn rotation
    // affordance for the rotate nav button. Spins the content
    // clockwise 90° per click; the icon's CW arrow matches.
    // Single clockwise arrow — one loop, one arrowhead. The old glyph was a
    // two-arrow cycle that read the same as the old reset icon.
    rotate:  '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"/><path stroke-linecap="round" stroke-linejoin="round" d="M21 3v5h-5"/></svg>',
    // Counter-clockwise twin of `rotate` — the same single-arrow glyph
    // mirrored on X (matrix(-1 0 0 1 24 0)), so the arrowhead reads top-left
    // and the loop spins CCW. A guaranteed visual mirror of the CW button
    // rather than a hand-drawn second glyph.
    rotateLeft: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true"><g transform="matrix(-1 0 0 1 24 0)"><path stroke-linecap="round" stroke-linejoin="round" d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"/><path stroke-linecap="round" stroke-linejoin="round" d="M21 3v5h-5"/></g></svg>',
    // "Actual size": the zoom glass with a 1 in it, after the photo apps —
    // one image pixel to one screen pixel. Same lens as zoom in / out, so
    // the three read as one family: − 1 +.
    actualSize: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607ZM9.25 8.5l1.5-1.25v6.5"/></svg>',
    // Heroicons `ellipsis-horizontal` — the nav's overflow: the buttons
    // that did not fit wait behind it.
    more: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M6.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM12.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM18.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z"/></svg>'
  };

  // Snap any rotation input to the nearest multiple of 90 and
  // normalize to [0, 360). Used everywhere rotation is accepted as
  // input; keeps the engine's `rot` state in a closed set of
  // {0, 90, 180, 270} so the trig math is exact (no float drift)
  // and the CSS transform string never carries a fractional angle.
  function normalizeRotation(deg) {
    if (typeof deg !== "number" || !isFinite(deg)) return 0;
    var snapped = Math.round(deg / 90) * 90;
    return ((snapped % 360) + 360) % 360;
  }

  // Exact cosine + sine for the four snapped rotations. Avoids
  // running `Math.cos`/`Math.sin` per coordinate transform and
  // guarantees the 90° / 270° cases produce a clean 0 (rather than
  // ~6e-17, which compounds over many transforms).
  function rotationCosSin(rot) {
    switch (rot) {
      case 0:   return { c:  1, sn:  0 };
      case 90:  return { c:  0, sn:  1 };
      case 180: return { c: -1, sn:  0 };
      case 270: return { c:  0, sn: -1 };
    }
    var r = rot * Math.PI / 180;
    return { c: Math.cos(r), sn: Math.sin(r) };
  }

  // ===========================================================================
  // Styles — one stylesheet for viewer, canvas, and strip. The six --fresco-*
  // custom properties are the entire palette surface (system / light / dark /
  // inherit branches below). Structural rules apply regardless of theme.
  // ===========================================================================

  var stylesInjected = false;
  function injectStyles() {
    if (stylesInjected) return;
    // No head yet (script in <head>, pre-parse) — the load-time call below
    // retries on DOMContentLoaded, and every hook mount calls this too.
    if (typeof document === "undefined" || !document.head) return;
    stylesInjected = true;

    var css = [
      // ── Nav buttons ─────────────────────────────────────────────────────
      ".fresco-nav {",
      "  position: absolute; top: 12px; left: 12px; z-index: 10;",
      "  display: flex; flex-direction: column; gap: 6px;",
      "  pointer-events: auto;",
      // The room the nav may take. What does not fit moves behind the
      // "more" button rather than running off the viewer (see enhanceNav).
      "  max-height: calc(100% - 24px);",
      "}",
      // A row along the top. `--fresco-nav-reserve-end` is the host's own
      // chrome at the far end of that edge (a close button, say): the row
      // stops short of it and overflows instead of running underneath.
      ".fresco-nav[data-layout=\"row\"] {",
      "  flex-direction: row; max-height: none;",
      "  max-width: calc(100% - 12px - var(--fresco-nav-reserve-end, 12px));",
      "}",
      // The overflow popover: a second strip, across the nav's direction.
      // `.fresco-nav` too, so the buttons are styled alike and a press in it
      // is chrome to the gesture handlers, not the start of a pan.
      ".fresco-nav.fresco-nav-overflow {",
      "  top: auto; left: auto; max-width: none; max-height: none;",
      "  z-index: 11; flex-direction: row; padding: 6px; border-radius: 10px;",
      "  background: var(--fresco-nav-overflow-bg, rgba(0, 0, 0, 0.25));",
      "}",
      ".fresco-nav[data-layout=\"row\"] + .fresco-nav-overflow { flex-direction: column; }",
      // Mirrored: the first buttons sit at the INNER end of the row and
      // "more" out in the corner — for a host whose main control should be
      // the one nearest the middle of the screen, not the farthest away.
      ".fresco-nav[data-layout=\"row\"][data-reverse] { flex-direction: row-reverse; }",
      ".fresco-nav [hidden], .fresco-nav-overflow[hidden] { display: none !important; }",
      ".fresco-nav button {",
      "  flex: none; width: 36px; height: 36px;",
      "  display: inline-flex; align-items: center; justify-content: center;",
      "  border: none; padding: 0; cursor: pointer;",
      "  background: var(--fresco-nav-bg); color: var(--fresco-nav-fg);",
      "  border-radius: 8px;",
      "  transition: background 120ms ease;",
      "}",
      ".fresco-nav button:hover { background: var(--fresco-nav-bg-hover); }",
      ".fresco-nav button:focus-visible {",
      "  outline: 2px solid var(--fresco-nav-focus); outline-offset: 1px;",
      "}",
      ".fresco-nav svg { width: 18px; height: 18px; }",

      // ── Zoom control (`actual_size`) ────────────────────────────────────
      // The percentage, always showing, in the bottom-right corner; hover
      // it (or tap it) and a slider opens to its left. Dark whatever the
      // theme: it sits on the picture. A host lifts it clear of its own
      // bottom chrome with `--fresco-zoom-bottom` / `--fresco-zoom-right`.
      ".fresco-zoom {",
      "  position: absolute; z-index: 10;",
      "  right: var(--fresco-zoom-right, 12px); bottom: var(--fresco-zoom-bottom, 12px);",
      "  display: flex; align-items: center; gap: 2px;",
      "  height: 32px; padding: 0 4px; border-radius: 999px;",
      "  background: var(--fresco-zoom-bg, rgba(0, 0, 0, 0.6)); color: #fff;",
      "  font: 600 13px/1 system-ui, -apple-system, sans-serif;",
      "  font-variant-numeric: tabular-nums;",
      "  user-select: none; -webkit-user-select: none; touch-action: none;",
      "  transition: bottom 160ms ease;",
      "}",
      ".fresco-zoom-pct {",
      "  border: none; background: none; color: inherit; font: inherit;",
      "  min-width: 48px; height: 28px; padding: 0 6px; border-radius: 999px; cursor: pointer;",
      "}",
      ".fresco-zoom-pct:focus-visible, .fresco-zoom-track:focus-visible {",
      "  outline: 2px solid var(--fresco-nav-focus, #3b82f6); outline-offset: 1px;",
      "}",
      ".fresco-zoom-track {",
      "  position: relative; width: 0; height: 28px; overflow: hidden;",
      "  cursor: pointer; transition: width 160ms ease, margin 160ms ease;",
      "}",
      ".fresco-zoom[data-open] .fresco-zoom-track { width: 168px; margin-left: 10px; overflow: visible; }",
      ".fresco-zoom-rail {",
      "  position: absolute; left: 0; right: 0; top: 50%; height: 3px; margin-top: -1.5px;",
      "  border-radius: 2px; background: rgba(255, 255, 255, 0.3);",
      "}",
      ".fresco-zoom-tick {",
      "  position: absolute; top: 50%; width: 2px; height: 9px; margin: -4.5px 0 0 -1px;",
      "  border-radius: 1px; background: rgba(255, 255, 255, 0.75);",
      "}",
      ".fresco-zoom-thumb {",
      "  position: absolute; top: 50%; width: 14px; height: 14px; margin: -7px 0 0 -7px;",
      "  border-radius: 50%; background: #fff; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);",
      "  pointer-events: none;",
      "}",
      ".fresco-zoom[data-detent] .fresco-zoom-thumb { transform: scale(1.15); }",

      // ── Host theming ────────────────────────────────────────────────────
      ".fresco-viewer:not([data-fresco-theme=\"inherit\"]) {",
      "  --fresco-bg: #fafafa;",
      "  --fresco-grid-dot: #d4d4d8;",
      "  --fresco-nav-bg: rgba(0, 0, 0, 0.55);",
      "  --fresco-nav-bg-hover: rgba(0, 0, 0, 0.78);",
      "  --fresco-nav-fg: #fff;",
      "  --fresco-nav-focus: rgba(255, 255, 255, 0.7);",
      "}",
      // `touch-action: none` is critical for iOS Safari (blocks browser pinch).
      // `user-select: none` blocks i-beam highlight on the host.
      // `-webkit-touch-callout: none` is the other half of that on iOS: a long
      // press raises the selection/callout UI there THROUGH `user-select:
      // none`, which is how a finger resting on a canvas ends up highlighting
      // the page behind it. Desktop browsers ignore the property.
      // `cursor: grab` signals draggability; engine swaps to `grabbing` via class.
      ".fresco-viewer {",
      "  position: relative; overflow: hidden;",
      "  touch-action: none;",
      "  user-select: none;",
      "  -webkit-user-select: none;",
      "  -webkit-touch-callout: none;",
      "  cursor: grab;",
      "  background-color: var(--fresco-bg);",
      "  background-image: radial-gradient(circle, var(--fresco-grid-dot) 1px, transparent 1px);",
      "  background-size: 24px 24px;",
      "  outline: none;",
      "}",
      ".fresco-viewer.fresco--dragging { cursor: grabbing; }",
      // The dot grid is a reading aid, not part of anyone's drawing. Some
      // boards want a plain surface — a photo to annotate, a presentation —
      // and the dots read as noise over the top of those.
      ".fresco-viewer.fresco--no-grid { background-image: none; }",
      // The grid is pinned to the canvas: the engine moves and scales it with
      // every pan and zoom (see syncGrid), so it reads as a board lying
      // behind the picture — the same dots throughout, spreading out and
      // drawing in. Behind @supports because the fade at extreme zoom-out
      // needs color-mix(); without it the grid stays the fixed one above.
      //
      // Keyed on the variables alone (each with the static grid as its
      // fallback), not on an attribute the engine adds: a LiveView host
      // re-rendering the viewer strips attributes its template doesn't
      // have, and every saved drawing snapped the grid back to the static
      // 24px one until the next pan.
      "@supports (color: color-mix(in srgb, red 50%, transparent)) {",
      "  .fresco-viewer {",
      "    background-image: radial-gradient(circle,",
      "      color-mix(in srgb, var(--fresco-grid-dot) var(--fresco-grid-alpha, 100%), transparent) var(--fresco-grid-r, 1px),",
      "      transparent calc(var(--fresco-grid-r, 1px) + 0.75px));",
      "    background-size: var(--fresco-grid-pitch, 24px) var(--fresco-grid-pitch, 24px);",
      "    background-position: var(--fresco-grid-x, 0px) var(--fresco-grid-y, 0px);",
      "  }",
      "  .fresco-viewer.fresco--no-grid { background-image: none; }",
      "}",
      // Stage: transformed surface holding the image(s). `transform-origin: 0 0`
      // pairs with the engine's (tx, ty, s) math. `will-change: transform` +
      // `backface-visibility: hidden` keep the layer permanently composited.
      ".fresco-stage {",
      "  position: absolute; top: 0; left: 0;",
      "  transform-origin: 0 0;",
      "  will-change: transform;",
      "  backface-visibility: hidden;",
      "  -webkit-backface-visibility: hidden;",
      "}",
      // Stage <img>s must NOT be CSS-shrunk by framework resets. The engine
      // sizes each img inline via width/height per frame; Tailwind preflight's
      // `img { max-width: 100% }` would override our math without this.
      ".fresco-stage img {",
      "  display: block;",
      "  max-width: none;",
      "  max-height: none;",
      "  width: auto;",
      "  height: auto;",
      "  user-select: none;",
      "  -webkit-user-drag: none;",
      "  pointer-events: none;",
      "}",
      // Hide the image until the engine has run the first fit. Without this,
      // a high-resolution image (e.g. 30000×20000) renders at its natural
      // CSS pixel size during the gap between the img element appearing in
      // the DOM and the engine reading naturalWidth + calling applyChildren.
      // The viewer's `overflow: hidden` clips everything outside the
      // viewport — the user sees just the top-left chunk and can't pan to
      // the rest (clampPan has nothing to clamp against until iw/ih are
      // set). Toggling visibility via this class keeps the layout stable
      // (img still participates in stage sizing) while keeping the natural-
      // size flash invisible.
      // `[data-fresco-prefit]` is exempt: the server marks an image it has
      // already painted CSS-contained (Fresco.Canvas.image_style/2), which is
      // a fit — soft, but whole and in proportion. Hiding THAT would blank the
      // viewer for the whole stretch between this bundle loading and the hook
      // mounting, which on a slow connection is seconds. The engine strips the
      // attribute as it takes over, in the same tick that adds
      // `fresco--ready`, so there is no window where raw sizes show.
      ".fresco-viewer:not(.fresco--ready) .fresco-stage img:not([data-fresco-prefit]) {",
      "  visibility: hidden;",
      "}",
      // System mode: follow OS preference. Excluded for explicit light or inherit.
      "@media (prefers-color-scheme: dark) {",
      "  .fresco-viewer:not([data-fresco-theme=\"light\"]):not([data-fresco-theme=\"inherit\"]) {",
      "    --fresco-bg: #0a0a0a;",
      "    --fresco-grid-dot: #262626;",
      "    --fresco-nav-bg: rgba(255, 255, 255, 0.12);",
      "    --fresco-nav-bg-hover: rgba(255, 255, 255, 0.20);",
      "    --fresco-nav-fg: #fff;",
      "    --fresco-nav-focus: rgba(255, 255, 255, 0.7);",
      "  }",
      "}",
      ".fresco-viewer[data-fresco-theme=\"dark\"] {",
      "  --fresco-bg: #0a0a0a;",
      "  --fresco-grid-dot: #262626;",
      "  --fresco-nav-bg: rgba(255, 255, 255, 0.12);",
      "  --fresco-nav-bg-hover: rgba(255, 255, 255, 0.20);",
      "  --fresco-nav-fg: #fff;",
      "  --fresco-nav-focus: rgba(255, 255, 255, 0.7);",
      "}",
      ".fresco-viewer[data-fresco-theme=\"light\"] {",
      "  --fresco-bg: #fafafa;",
      "  --fresco-grid-dot: #d4d4d8;",
      "  --fresco-nav-bg: rgba(0, 0, 0, 0.55);",
      "  --fresco-nav-bg-hover: rgba(0, 0, 0, 0.78);",
      "  --fresco-nav-fg: #fff;",
      "  --fresco-nav-focus: rgba(255, 255, 255, 0.7);",
      "}",

      // Strip-host CSS lives in the separate `fresco_strip` package as
      // of fresco 0.6.0. Consumers using `<FrescoStrip.viewer>` import
      // `fresco_strip/priv/static/fresco_strip.js`, which injects its
      // own `.fresco-strip-*` rules on first hook mount.
      ""
    ].join("\n");

    var style = document.createElement("style");
    style.setAttribute("data-fresco", "");
    style.textContent = css;
    document.head.appendChild(style);
  }

  // The stylesheet must exist before the FIRST PAINT, not at hook mount.
  // `.fresco-viewer:not(.fresco--ready) .fresco-stage img` is what keeps the
  // server-rendered <img> — sized in CANVAS pixels, untransformed because the
  // engine has not run — off the screen until the first fit. Injecting it in
  // `mounted()` meant that rule did not exist for the whole gap between the
  // static render and the LiveView hook mounting, and a page that loads with
  // the viewer already on screen (deep link, refresh) painted the raw image
  // for that entire gap: Tailwind preflight's `img { max-width: 100% }`
  // clamps the inline width to the column while the inline height stays at
  // canvas px, so it showed up hugely magnified AND at the wrong aspect
  // ratio. Measured at over a second on a cold load; the bundle parses far
  // earlier than any hook mounts.
  //
  // Injecting from the bundle also keeps the fallback honest: if the script
  // never loads, the hiding rule never exists either, and the server markup
  // stays visible.
  if (typeof document !== "undefined") {
    if (document.head) injectStyles();
    else if (document.addEventListener) {
      document.addEventListener("DOMContentLoaded", injectStyles, { once: true });
    }
  }

  function makeButton(svg, title, onClick) {
    var btn = document.createElement("button");
    btn.type = "button";
    btn.title = title;
    btn.setAttribute("aria-label", title);
    btn.innerHTML = svg;
    btn.addEventListener("click", function(e) {
      e.preventDefault();
      e.stopPropagation();
      onClick();
    });
    return btn;
  }

  // ===========================================================================
  // Shared event-bus helper. Used by the viewer, canvas, and strip handles.
  // ===========================================================================

  function createEventBus() {
    var subscribers = {};

    return {
      on: function(eventName, handler) {
        subscribers[eventName] = subscribers[eventName] || [];
        subscribers[eventName].push(handler);
        return function unsubscribe() {
          var arr = subscribers[eventName] || [];
          var idx = arr.indexOf(handler);
          if (idx !== -1) arr.splice(idx, 1);
        };
      },

      _emit: function(eventName, payload) {
        var arr = subscribers[eventName] || [];
        for (var i = 0; i < arr.length; i++) {
          try { arr[i](payload); } catch (_) {}
        }
      }
    };
  }

  // ===========================================================================
  // Shared nav-button attach helper. Returns an unsubscribe function carrying
  // `.setIcon(svg) / .setTitle(text) / .el`. No-op when navEl is null.
  // ===========================================================================

  //
  // `opts.slot` (a number) puts the button at the START of the nav instead of
  // the end, ordered by slot — so an extension's main control can lead the
  // row whichever script happened to attach first. Slot 0 is first.
  function attachNavButton(navEl, svg, title, onClick, opts) {
    if (!navEl) return function noop() {};
    var btn = makeButton(svg, title, onClick);
    var nav = navEl._frescoNav;
    var slot = opts && typeof opts.slot === "number" ? opts.slot : null;
    if (nav) nav.add(btn, slot);
    else if (slot !== null) navEl.insertBefore(btn, navEl.firstChild);
    else navEl.appendChild(btn);
    var remove = function removeButton() {
      // It may be in the overflow popover rather than the nav itself.
      if (btn.parentNode) btn.parentNode.removeChild(btn);
      if (nav) nav.relayout();
    };
    remove.setIcon = function(nextSvg) { btn.innerHTML = nextSvg; };
    remove.setTitle = function(nextTitle) {
      btn.title = nextTitle;
      btn.setAttribute("aria-label", nextTitle);
    };
    remove.el = btn;
    return remove;
  }

  // ===========================================================================
  // View tracker — emits "view-focus" / "view-blur" events on the bus when
  // the dominant image on the host changes (or visibility flips). Shared
  // between <Fresco.canvas> (overlap-ratio dominance) and
  // <Fresco.scroll_strip> (existing currentImageIdx). The host supplies
  // `getDominantImageId() → string | null` and calls `tick()` when the
  // viewport changes; the tracker handles the settleMs gate, the focused-
  // state machine, the Page Visibility pause, and the event emits.
  //
  // Default off — callers explicitly invoke `enable(opts)` to start. Until
  // then the helper sits idle and emits nothing.
  // ===========================================================================

  function createViewTracker(opts) {
    var bus = opts.bus;
    var getDominantImageId = opts.getDominantImageId;
    var settleMs = (typeof opts.defaultSettleMs === "number") ? opts.defaultSettleMs : 150;
    var threshold = (typeof opts.defaultThreshold === "number") ? opts.defaultThreshold : 0.5;

    var enabled = false;
    var focusedImageId = null;
    var focusedAtMs = 0;
    var candidateImageId = null;
    var candidateSince = 0;
    var settleTimerId = null;
    var visibilityListener = null;

    function nowMs() {
      return (typeof performance !== "undefined" && performance.now)
        ? performance.now() : Date.now();
    }

    function clearSettleTimer() {
      if (settleTimerId) {
        try { clearTimeout(settleTimerId); } catch (_) {}
        settleTimerId = null;
      }
    }

    function commitChange(newId, reason) {
      var prev = focusedImageId;
      var prevAtMs = focusedAtMs;
      if (prev !== null && prev !== newId) {
        bus._emit("view-blur", {
          imageId: prev,
          durationMs: Math.max(0, nowMs() - prevAtMs),
          atMs: nowMs(),
          reason: reason || "viewport-change"
        });
      }
      if (newId !== null && newId !== prev) {
        focusedImageId = newId;
        focusedAtMs = nowMs();
        bus._emit("view-focus", {
          imageId: newId,
          previousImageId: prev,
          atMs: nowMs()
        });
      } else if (newId === null) {
        focusedImageId = null;
        focusedAtMs = 0;
      }
    }

    function tick() {
      if (!enabled) return;
      if (typeof document !== "undefined" && document.hidden) return;
      var dominant = null;
      try { dominant = getDominantImageId(threshold); } catch (_) {}
      if (dominant === candidateImageId) return;
      candidateImageId = dominant;
      candidateSince = nowMs();
      clearSettleTimer();
      if (dominant !== focusedImageId) {
        // Schedule a commit after the settle window. If the candidate
        // changes again before settle, the next tick clears this
        // timer and starts a new one.
        settleTimerId = setTimeout(function() {
          settleTimerId = null;
          if (enabled &&
              candidateImageId === dominant &&
              dominant !== focusedImageId) {
            commitChange(dominant, "viewport-change");
          }
        }, settleMs);
      }
    }

    function onVisibilityChange() {
      if (!enabled) return;
      if (typeof document !== "undefined" && document.hidden) {
        if (focusedImageId !== null) {
          commitChange(null, "page-hidden");
        }
        clearSettleTimer();
        candidateImageId = null;
        candidateSince = 0;
      } else {
        tick();
      }
    }

    function enable(o) {
      if (o && typeof o.settleMs === "number") settleMs = o.settleMs;
      if (o && typeof o.threshold === "number") threshold = o.threshold;
      if (enabled) {
        tick();
        return;
      }
      enabled = true;
      if (typeof document !== "undefined" && document.addEventListener) {
        visibilityListener = onVisibilityChange;
        document.addEventListener("visibilitychange", visibilityListener);
      }
      tick();
    }

    function disable(reason) {
      if (!enabled) return;
      if (focusedImageId !== null) {
        commitChange(null, reason || "disabled");
      }
      enabled = false;
      clearSettleTimer();
      candidateImageId = null;
      candidateSince = 0;
      if (visibilityListener && typeof document !== "undefined") {
        try {
          document.removeEventListener("visibilitychange", visibilityListener);
        } catch (_) {}
      }
      visibilityListener = null;
    }

    function getFocused() {
      if (!enabled || focusedImageId === null) return null;
      return {
        imageId: focusedImageId,
        durationSoFarMs: nowMs() - focusedAtMs,
        atMs: focusedAtMs
      };
    }

    return {
      enable: enable,
      disable: disable,
      tick: tick,
      getFocused: getFocused,
      isEnabled: function() { return enabled; }
    };
  }

  // ===========================================================================
  // Nav overlay — four buttons (fullscreen, zoom-in, zoom-out, reset). The
  // host element provides relative positioning (set in CSS), and the nav
  // attaches as a child so extensions can append more buttons via
  // `handle.appendNavButton(...)`.
  // ===========================================================================

  function buildNav(host, handlers, opts) {
    injectStyles();
    var nav = document.createElement("div");
    nav.className = "fresco-nav";
    // `opts.navButtonEnabled` is the consumer's allowlist gate. Default
    // (no gate) keeps every button — back-compat with pre-0.6 callers.
    var enabled = (opts && typeof opts.navButtonEnabled === "function")
      ? opts.navButtonEnabled
      : function() { return true; };
    // Each built-in is named: the overflow moves them by name, in
    // NAV_COLLAPSE order. Buttons an extension appends carry no name and
    // never move.
    function add(name, icon, title, handler) {
      var btn = makeButton(icon, title, handler);
      btn.setAttribute("data-fresco-nav", name);
      nav.appendChild(btn);
    }
    if (enabled("fullscreen")) add("fullscreen", ICONS.expand, "Toggle fullscreen", handlers.onFullscreen);
    if (enabled("zoom_in"))    add("zoom_in", ICONS.zoomIn, "Zoom in", handlers.onZoomIn);
    // Between the two zooms, so a mirrored row reads − 1 + and a column
    // + 1 −. Only for a host that zooms in real pixels (`actual_size`).
    if (enabled("actual_size") && handlers.onActualSize) {
      add("actual_size", ICONS.actualSize, "Actual size (100%)", handlers.onActualSize);
    }
    if (enabled("zoom_out"))   add("zoom_out", ICONS.zoomOut, "Zoom out", handlers.onZoomOut);
    // `rotate` is the original clockwise button (+90); `rotate_left` is the
    // counter-clockwise twin (-90), rendered just before it so the pair reads
    // ↺ ↻. Neither is in the default nav_buttons list, so a consumer opts each
    // in explicitly (or passes no list = all buttons, which shows both).
    if (enabled("rotate_left") && handlers.onRotateLeft) {
      add("rotate_left", ICONS.rotateLeft, "Rotate left 90°", handlers.onRotateLeft);
    }
    if (enabled("rotate") && handlers.onRotate) {
      add("rotate", ICONS.rotate, "Rotate right 90°", handlers.onRotate);
    }
    if (enabled("home"))       add("home", ICONS.reset, "Reset view", handlers.onFit);
    nav.setAttribute("data-layout", opts && opts.layout === "row" ? "row" : "column");
    if (opts && opts.reverse) nav.setAttribute("data-reverse", "");
    host.appendChild(nav);
    enhanceNav(nav, host, opts && Array.isArray(opts.overflow) ? opts.overflow : []);
    return nav;
  }

  // The order built-in buttons give up their place when the nav does not
  // fit: the ones a person reaches for least go first. Zoom and "reset
  // view" are what is left on the smallest screens, beside whatever an
  // extension added (Etcher's pencil and eye), which never move — a host
  // that put a button there wants it seen.
  var NAV_COLLAPSE = ["fullscreen", "rotate_left", "rotate", "actual_size", "zoom_out", "zoom_in", "home"];

  // Fits the nav to the room it has. Whatever does not fit moves into a
  // popover behind a "more" button at the end, and moves back as the room
  // returns — a phone held upright and then turned, a split view dragged
  // wider. Measured rather than set by breakpoints, because the room
  // depends on things only the page knows: the host's own chrome in the
  // corner (`--fresco-nav-reserve-end`), the buttons extensions added, and
  // which of those are showing right now.
  //
  // The nav carries `_frescoNav` so `attachNavButton` can slot an
  // extension's button in before "more" and ask for a re-fit.
  //
  // `pinned` names built-ins the host wants behind "more" whatever the room
  // (`nav_overflow`) — a short row of the basics, with the rest a press away.
  function enhanceNav(nav, host, pinned) {
    pinned = pinned || [];
    var order = 0;
    var kids = nav.children;
    for (var i = 0; i < kids.length; i++) kids[i]._frescoOrder = order++;

    var more = makeButton(ICONS.more, "More", function() { setOpen(pop.hidden); });
    more.setAttribute("data-fresco-nav-more", "");
    more.setAttribute("aria-expanded", "false");
    more.hidden = true;
    nav.appendChild(more);

    var pop = document.createElement("div");
    pop.className = "fresco-nav fresco-nav-overflow";
    pop.hidden = true;
    host.insertBefore(pop, nav.nextSibling);

    var scheduled = false;
    var torn = false;

    function row() { return nav.getAttribute("data-layout") === "row"; }

    function fits() {
      return row()
        ? nav.scrollWidth <= nav.clientWidth + 1
        : nav.scrollHeight <= nav.clientHeight + 1;
    }

    function slotBack(btn) {
      var ref = null;
      var cur = nav.children;
      for (var i = 0; i < cur.length; i++) {
        if (cur[i] === more || cur[i]._frescoOrder > btn._frescoOrder) { ref = cur[i]; break; }
      }
      nav.insertBefore(btn, ref);
    }

    function relayout() {
      scheduled = false;
      if (torn) return;
      // Not laid out (a hidden tab, a closed modal): nothing to measure, and
      // measuring would collapse every button into the popover.
      if (!host.clientWidth || !host.clientHeight) return;
      while (pop.firstChild) slotBack(pop.firstChild);
      more.hidden = true;
      for (var p = 0; p < pinned.length; p++) {
        var keep = nav.querySelector('[data-fresco-nav="' + pinned[p] + '"]');
        if (keep) { pop.appendChild(keep); more.hidden = false; }
      }
      if (!fits()) {
        more.hidden = false;
        for (var i = 0; i < NAV_COLLAPSE.length && !fits(); i++) {
          var btn = nav.querySelector('[data-fresco-nav="' + NAV_COLLAPSE[i] + '"]');
          if (btn) pop.appendChild(btn);
        }
        // Keep the popover in the nav's own order, whatever order they left in.
        Array.prototype.slice.call(pop.children)
          .sort(function(a, b) { return a._frescoOrder - b._frescoOrder; })
          .forEach(function(b) { pop.appendChild(b); });
      }
      if (!pop.firstChild) { more.hidden = true; setOpen(false); }
      else if (!pop.hidden) place();
    }

    function schedule() {
      if (scheduled || torn) return;
      scheduled = true;
      (window.requestAnimationFrame || setTimeout)(relayout);
    }

    function place() {
      if (row()) {
        pop.style.top = (nav.offsetTop + nav.offsetHeight + 6) + "px";
        pop.style.left = (nav.offsetLeft + more.offsetLeft - 6) + "px";
      } else {
        pop.style.left = (nav.offsetLeft + nav.offsetWidth + 6) + "px";
        pop.style.top = (nav.offsetTop + more.offsetTop - 6) + "px";
      }
    }

    function onOutside(e) {
      var t = e.target;
      if (t && (pop.contains(t) || more.contains(t))) return;
      setOpen(false);
    }
    function onKey(e) { if (e.key === "Escape") setOpen(false); }

    function setOpen(open) {
      if (open && !pop.firstChild) open = false;
      pop.hidden = !open;
      more.setAttribute("aria-expanded", open ? "true" : "false");
      if (open) {
        place();
        document.addEventListener("pointerdown", onOutside, true);
        document.addEventListener("keydown", onKey);
      } else {
        document.removeEventListener("pointerdown", onOutside, true);
        document.removeEventListener("keydown", onKey);
      }
    }

    var ro = null;
    if (typeof ResizeObserver === "function") {
      // The host for the room, the nav for a button shown or hidden.
      ro = new ResizeObserver(schedule);
      ro.observe(host);
      ro.observe(nav);
    }

    nav._frescoNav = {
      add: function(btn, slot) {
        // Slotted buttons sort ahead of everything (built-ins start at 0);
        // the rest join the end, before "more". The nav is kept in order
        // so a built-in coming back from the popover finds its place.
        btn._frescoOrder = typeof slot === "number" ? slot - 1000 : order++;
        var ref = more;
        var cur = nav.children;
        for (var i = 0; i < cur.length; i++) {
          if (cur[i] !== more && cur[i]._frescoOrder > btn._frescoOrder) { ref = cur[i]; break; }
        }
        nav.insertBefore(btn, ref);
        schedule();
      },
      relayout: schedule,
      setLayout: function(layout) {
        nav.setAttribute("data-layout", layout === "row" ? "row" : "column");
        setOpen(false);
        schedule();
      },
      teardown: function() {
        torn = true;
        setOpen(false);
        if (ro) { try { ro.disconnect(); } catch (_) {} ro = null; }
        if (pop.parentNode) pop.parentNode.removeChild(pop);
      },
      // For tests and peers: the buttons waiting behind "more".
      collapsed: function() { return Array.prototype.slice.call(pop.children); }
    };
    schedule();
  }

  // ===========================================================================
  // Apply opt-in constraint data-attributes to a freshly-mounted engine. Reads
  // `data-zoom-floor`, `data-zoom-ceiling`, `data-pan-locked` off the host
  // element; missing/empty attrs are no-ops. Used by both mountFrescoViewer
  // and mountFrescoCanvas. Consumers can also call the engine setters at
  // runtime via the handle.
  // ===========================================================================

  // Read all 0.5.1+/0.6.0 declarative data-attrs off the host element
  // and return them as a single opts object. Used by both mount
  // functions to pre-configure the engine BEFORE it builds the nav
  // overlay (so the nav allowlist is honored on first paint, not after
  // a flash of the full button set).
  function readConstraintAttrs(el) {
    var opts = {};
    var floor = parseFloat(el.dataset.zoomFloor);
    if (!isNaN(floor) && floor > 0) opts.zoomFloor = floor;
    var ceil = parseFloat(el.dataset.zoomCeiling);
    if (!isNaN(ceil) && ceil > 0) opts.zoomCeiling = ceil;
    if (el.dataset.panLocked === "true") opts.panLocked = true;
    if (el.dataset.invertTwoFingerPan === "true") opts.invertTwoFingerPan = true;
    if (el.dataset.panCentre === "bounds") opts.panCentreBounds = true;
    if (el.dataset.actualSize === "true") opts.actualSize = true;
    if (el.dataset.navLayout === "row") opts.navLayout = "row";
    if (el.dataset.navReverse === "true") opts.navReverse = true;
    if (el.dataset.navOverflow) {
      opts.navOverflow = el.dataset.navOverflow.split(",").map(function(s) { return s.trim(); }).filter(Boolean);
    }
    // `data-gestures` mirrors the `data-nav-buttons` semantics
    // — see comment above the nav-buttons branch for the "none"
    // sentinel rationale.
    if (el.dataset.gestures === "none") {
      opts.gestures = [];
    } else if (el.dataset.gestures) {
      var gs = el.dataset.gestures.split(",").map(function(s) { return s.trim(); }).filter(Boolean);
      if (gs.length > 0) opts.gestures = gs;
    }
    // `data-nav-buttons` semantics:
    //   - attribute absent (`undefined`) → default, all buttons enabled
    //   - `"none"` → explicit hide-all (consumer passed an empty list)
    //   - CSV of names → allowlist of just those buttons
    // The "none" sentinel lets the Elixir side encode the
    // "hide-everything" intent without overloading "empty string"
    // (Phoenix sometimes drops empty data-attrs in render).
    if (el.dataset.navButtons === "none") {
      opts.navButtons = [];
    } else if (el.dataset.navButtons) {
      var bs = el.dataset.navButtons.split(",").map(function(s) { return s.trim(); }).filter(Boolean);
      if (bs.length > 0) opts.navButtons = bs;
    }
    var initRot = parseFloat(el.dataset.initialRotation);
    if (!isNaN(initRot)) opts.rotation = initRot;
    return opts;
  }

  // Apply attrs that take effect AFTER the engine + nav are constructed
  // (runtime overrides whose values are already set inside the engine
  // via createTransformEngine opts; the post-construct call is for
  // anything that needs `requestFrame` after fit, etc.). Kept for
  // back-compat shape; currently a no-op since readConstraintAttrs
  // covers everything.
  function applyConstraintAttrs(/* el, engine */) { /* no-op */ }

  // ===========================================================================
  // Shared transform engine — drives both <Fresco.viewer> (single image) and
  // <Fresco.canvas> (N images at canvas-pixel coords). The math is identical;
  // only what gets sized per-frame differs.
  //
  // Callers provide:
  //   getNaturalSize() → {w, h}   // viewer: image natural dims; canvas: canvas extent
  //   applyChildren(s)             // viewer: resize one <img>; canvas: re-layout all <img>s
  //
  // The engine owns state (tx, ty, s, vw, vh, …), gestures, fit/clamp math,
  // ResizeObserver, the nav overlay, the event bus, and teardown.
  // ===========================================================================

  function createTransformEngine(opts) {
    var el             = opts.el;
    var stage          = opts.stage;
    var getNaturalSize = opts.getNaturalSize;
    var applyChildren  = opts.applyChildren;
    var infiniteCanvas = !!opts.infiniteCanvas;
    // 0.6.0 — opt-in initial configuration from the host's data-attrs
    // (parsed by readConstraintAttrs). All optional / undefined-safe.
    var initialZoomFloor   = opts.zoomFloor;
    var initialZoomCeiling = opts.zoomCeiling;
    var initialPanLocked   = !!opts.panLocked;
    var initialInvertTwoFingerPan = !!opts.invertTwoFingerPan;
    // `pan_centre: :bounds` — see clampPan.
    var panCentreBounds = !!opts.panCentreBounds;
    // Zoom in real pixels (`actual_size`): 100% is one natural pixel per
    // physical screen pixel, and the home view is fit or 100%, whichever
    // is smaller — see `sHome`.
    var actualSize = !!opts.actualSize;
    var initialGestures    = opts.gestures;     // array or undefined
    var initialNavButtons  = opts.navButtons;   // array or undefined
    var initialRotation    = opts.rotation;     // number or undefined

    // ── State ──────────────────────────────────────────────────────────────
    var tx = 0, ty = 0, s = 1;
    // Rotation around the canvas/image origin, in degrees, snapped to
    // {0, 90, 180, 270}. Composed with translate + scale as:
    //   screen = translate(tx, ty) · rotate(rot) · scale(s) · image
    // The CSS transform on the stage mirrors that order:
    //   `translate3d(tx, ty, 0) rotate(rot deg)`.
    // Image-pixel math (imageToScreen / screenToImage / fit / clamp)
    // applies the rotation analytically — see `rotationCosSin`.
    var rot = normalizeRotation(initialRotation || 0);
    // The dot grid, on unless someone turns it off. Held here rather than
    // read back off the class each time so the answer survives a re-render.
    var gridVisible = true;
    // The rotation "reset view" returns to — the mount-time rotation, so
    // `resetView()` (Reset-view button + "0" key) undoes the +90° rotate
    // clicks without discarding a consumer's persisted `initial_rotation`.
    var homeRotation = rot;
    var nw = 0, nh = 0;          // natural extent (image natural for viewer; canvas dims for canvas)
    var vw = 0, vh = 0;          // viewport
    var sFit = 1, sMin = 1, sMax = 8;
    // The scale "home" means: where the picture opens, where reset view and
    // a double tap's way out land, and what counts as fully zoomed out for a
    // swipe. sFit, unless the host zooms in real pixels — then a picture
    // smaller than the viewer stays at its real size (sActual) instead of
    // being blown up, blurred, to fill it.
    var sHome = 1;
    var frameRequested = false;
    var ready = false;
    var bus = createEventBus();
    var pointers = new Map();
    var gestureStart = null;
    // One-finger swipe tracking (see `endSwipe`). Started by the first
    // finger down, spoiled by a second one, read when the last one lifts.
    var swipe = null;

    // ── Consumer-controlled overrides (opt-in; null/false = engine defaults) ─
    // `customSMin` / `customSMax` shadow sMin / sMax in recomputeBounds.
    // `panLocked` ignores single-pointer pan (drag + arrow-key); pinch
    // still works for zoom. `customPanBounds` clamps pan to a custom rect
    // in canvas-pixel coords (overrides infiniteCanvas's no-clamp).
    // `customHome` overrides the reset-button / `0`-key behavior with a
    // consumer-supplied function. `enabledGestures` / `enabledNavButtons`
    // are allowlists for gestures and built-in nav buttons (null = all
    // enabled).
    //
    // All defaults preserve pre-0.6 behavior — consumers opt in explicitly.
    var customSMin = (typeof initialZoomFloor === "number") ? initialZoomFloor : null;
    var customSMax = (typeof initialZoomCeiling === "number") ? initialZoomCeiling : null;
    var panLocked = initialPanLocked;
    // Two-finger pan's direction, flipped. The browser hands over the
    // fingers' movement already run through the OS's own scroll-direction
    // setting, and nothing in the event says which setting that was, so
    // the default can only suit most machines — this is for the rest.
    var twoFingerPanInverted = initialInvertTwoFingerPan;
    // Which mouse button started the current drag (0 = left, 1 = middle).
    // Reset when the last pointer lifts. Non-mouse pointers report 0.
    var panButton = 0;
    var customPanBounds = null;      // {x, y, width, height} / null
    var customHome = null;           // function / null
    // Deadline (ms since epoch) until which the next `tap` event
    // emission is swallowed. Set by `suppressNextTap(ms?)` — peer
    // libraries (Etcher, ML overlays) call it after committing a
    // gesture that would otherwise race the OS-synthesized
    // mousedown/mouseup → tap pipeline. Default window 250 ms is
    // a comfortable margin for iOS Safari's synthesized events.
    var suppressTapUntil = 0;
    var enabledGestures = Array.isArray(initialGestures) ? new Set(initialGestures) : null;
    var enabledNavButtons = Array.isArray(initialNavButtons) ? new Set(initialNavButtons) : null;

    // ── Math ───────────────────────────────────────────────────────────────
    function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }

    // Effective natural dims accounting for rotation. At 90° / 270°
    // the content's screen-space width and height swap, so the
    // fit-to-viewport scale must compare viewport against the
    // swapped dims — otherwise a rotated portrait image would fit
    // against its unrotated landscape and end up too small.
    function effectiveNaturalSize() {
      return (rot === 90 || rot === 270) ? { w: nh, h: nw } : { w: nw, h: nh };
    }

    // Bounding box of the canvas content in stage-local coords
    // (post-rotation + scale, pre-translate), assuming canvas is
    // (0, 0)–(nw, nh). For 90°-snapped rotations this is a swap +
    // flip, no trig at runtime.
    function rotatedContentBBox() {
      switch (rot) {
        case 0:   return { minX: 0,        minY: 0,        maxX: nw * s, maxY: nh * s };
        case 90:  return { minX: -nh * s,  minY: 0,        maxX: 0,      maxY: nw * s };
        case 180: return { minX: -nw * s,  minY: -nh * s,  maxX: 0,      maxY: 0 };
        default:  return { minX: 0,        minY: -nw * s,  maxX: nh * s, maxY: 0 }; // 270
      }
    }

    // The scale at which one natural pixel covers one PHYSICAL screen pixel:
    // what "100%" means. Read fresh each time, because devicePixelRatio
    // moves — a window dragged to another monitor, the browser zoomed.
    function sActual() {
      var d = (typeof window !== "undefined" && window.devicePixelRatio) || 1;
      return 1 / d;
    }

    function recomputeBounds() {
      var eff = effectiveNaturalSize();
      if (eff.w > 0 && eff.h > 0 && vw > 0 && vh > 0) {
        sFit = Math.min(vw / eff.w, vh / eff.h);
      } else {
        sFit = 1;
      }
      sHome = actualSize ? Math.min(sFit, sActual()) : sFit;
      var defaultSMin = infiniteCanvas ? sHome * 0.05 : sHome;
      sMin = (typeof customSMin === "number") ? customSMin : defaultSMin;
      // The home view is always reachable, whatever floor a host or peer
      // set — Etcher's floor, for one, is worked out from the fitted view.
      if (actualSize && sMin > sHome) sMin = sHome;
      // Cap on the rendered CSS size in either axis. Most browsers
      // refuse to lay out elements larger than ~32767 px (signed
      // 16-bit); we use 30000 as a safe floor across Chrome / Safari
      // / Firefox. The 0.4.x-era 8192 cap was a GPU-layer-texture
      // safety for the transform-scale engine; the width/height
      // engine in 0.5.x doesn't have that constraint, so the only
      // real ceiling is the browser's max element size.
      var MAX_RENDERED_PX = 30000;
      var renderedCap = MAX_RENDERED_PX / Math.max(nw || 1, nh || 1);
      // Default ceiling is 8× natural pixel ratio (matches OSD's
      // legacy `maxZoomPixelRatio: 8`). Take whichever of {8, renderedCap}
      // is smaller for browser safety, but never go below sFit
      // (otherwise the user can't zoom in at all).
      var defaultSMax = Math.min(8, renderedCap);
      if (defaultSMax < sFit) defaultSMax = sFit;
      sMax = (typeof customSMax === "number") ? customSMax : defaultSMax;
      if (sMax < sMin) sMax = sMin;
    }

    // Rotate + scale an unrotated canvas rect to its stage-local
    // bounding box (post-rotation, pre-translate). Used by clampPan
    // for both the canvas content rect (rotatedContentBBox) and any
    // consumer-supplied custom pan bounds — keeps the math in one
    // place regardless of which rect we're clamping against.
    function rotatedRectBBox(rect) {
      var cs = rotationCosSin(rot);
      var x1 = rect.x, y1 = rect.y;
      var x2 = rect.x + rect.width, y2 = rect.y + rect.height;
      var minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      var pts = [[x1, y1], [x2, y1], [x2, y2], [x1, y2]];
      for (var i = 0; i < 4; i++) {
        var px = pts[i][0] * s * cs.c - pts[i][1] * s * cs.sn;
        var py = pts[i][0] * s * cs.sn + pts[i][1] * s * cs.c;
        if (px < minX) minX = px;
        if (px > maxX) maxX = px;
        if (py < minY) minY = py;
        if (py > maxY) maxY = py;
      }
      return { minX: minX, minY: minY, maxX: maxX, maxY: maxY };
    }

    function clampPan() {
      // setPanBounds takes precedence over infiniteCanvas. The consumer is
      // explicitly opting into clamping for a custom rect; the "no clamp"
      // contract of infinite_canvas only applies when no rect is set.
      // Both branches use the same rotated-bbox math so a rotated
      // viewer still clamps to "content covers the viewport" instead
      // of clamping against the unrotated rect (which would leak the
      // background past the rotated content's edges).
      var bbox;
      if (customPanBounds) {
        bbox = rotatedRectBBox(customPanBounds);
      } else {
        if (infiniteCanvas) return;
        bbox = rotatedContentBBox();
      }
      var bw = bbox.maxX - bbox.minX;
      var bh = bbox.maxY - bbox.minY;
      // Too small to pan in an axis? Then the position is fixed, and what
      // gets centred is the CONTENT — never a custom rect. A consumer sets
      // bounds to say how far the view may travel (Etcher widens them to
      // reach ink drawn outside the picture); it is not saying the picture
      // should move. Centring the rect did exactly that: ink spilling off
      // one side shoved the picture the other way, by half the spill, the
      // moment the annotations loaded — the image visibly settling
      // sideways under the viewer.
      //
      // Unless the host asks for the bounds to be centred (`pan_centre:
      // :bounds`): a host showing the same drawing elsewhere as one flat
      // picture of the picture-plus-ink wants this view framed like it.
      var centre = (customPanBounds && !panCentreBounds) ? rotatedContentBBox() : bbox;
      if (bw >= vw) {
        tx = clamp(tx, vw - bbox.maxX, -bbox.minX);
      } else {
        tx = (vw - (centre.maxX - centre.minX)) / 2 - centre.minX;
      }
      if (bh >= vh) {
        ty = clamp(ty, vh - bbox.maxY, -bbox.minY);
      } else {
        ty = (vh - (centre.maxY - centre.minY)) / 2 - centre.minY;
      }
    }

    // Re-read natural size + viewport from the DOM, recompute bounds, re-clamp.
    // Use this when source dimensions changed (image load, layout swap) but
    // you don't want to force a refit (preserves user's current zoom intent).
    function refresh() {
      var n = getNaturalSize();
      nw = n.w || 0;
      nh = n.h || 0;
      var rect = el.getBoundingClientRect();
      vw = rect.width;
      vh = rect.height;
      recomputeBounds();
      if (s < sMin) s = sMin;
      if (s > sMax) s = sMax;
      clampPan();
    }

    // Home: the scale in `sHome`, centred. Named for what it did before
    // `actual_size` existed, when home and fit were the same thing.
    function fit() { frameAt(function() { return sHome; }); }

    // Fit the whole picture to the viewer, even when home is smaller.
    function fitToView() { frameAt(function() { return sFit; }); }

    // `scale` is a function because refresh() is what works the numbers
    // out, and it has to run first.
    function frameAt(scale) {
      refresh();
      s = scale();
      // Center the rotated bbox in the viewport. For rot=0 this
      // reduces to the previous formula; for 90 / 180 / 270 the
      // bbox extents are different and we'd otherwise frame the
      // wrong rect.
      var bbox = rotatedContentBBox();
      var bw = bbox.maxX - bbox.minX;
      var bh = bbox.maxY - bbox.minY;
      tx = (vw - bw) / 2 - bbox.minX;
      ty = (vh - bh) / 2 - bbox.minY;
      clampPan();
      requestFrame();
    }

    function zoomAt(px, py, k) {
      var s2 = clamp(s * k, sMin, sMax);
      if (s2 === s) return;
      var kEff = s2 / s;
      tx = px - (px - tx) * kEff;
      ty = py - (py - ty) * kEff;
      s = s2;
      clampPan();
      emitZoom();
      requestFrame();
    }

    function panBy(dx, dy) {
      if (panLocked) return;
      panRaw(dx, dy);
    }

    // The move itself, with no lock consulted. `panLocked` exists to free
    // the LEFT drag for something else — a marquee, a drawing tool — which
    // is why a middle drag and a pinch are already exempt from it (see the
    // pointermove comment). Two fingers on a trackpad are in that same
    // company: they are not the gesture the lock was taken out on, and a
    // board you cannot pan while a tool is armed is the state the lock was
    // supposed to make usable.
    function panRaw(dx, dy) {
      tx += dx; ty += dy;
      clampPan();
      bus._emit("pan", { tx: tx, ty: ty });
      requestFrame();
    }

    function setTransform(nextTx, nextTy, nextS, nextRot) {
      // Any direct setTransform cancels an in-flight animation —
      // it's an explicit "go here now" command, not a "glide here"
      // request. Use animateTo() if you want the animated variant.
      cancelAnimation();
      tx = nextTx; ty = nextTy;
      s = clamp(nextS, sMin, sMax);
      // Optional 4th arg — keep 3-arg callers (pre-0.5.7) on their
      // current rotation. setRotation() is the dedicated API for
      // rotation-only changes; this lets composite ops set all four
      // values atomically without re-running fit math.
      if (typeof nextRot === "number") rot = normalizeRotation(nextRot);
      clampPan();
      requestFrame();
    }

    // ── Animated transitions (opt-in via handle.fitBounds(rect, {animate})) ─
    // rAF-driven interpolation between current and target (tx, ty, s).
    // Cancellable on user gesture (pointerdown / wheel / dblclick) so
    // the in-flight glide never blocks the user's intent.
    var anim = null;

    var easings = {
      linear:      function(t) { return t; },
      "ease-out":  function(t) { return 1 - Math.pow(1 - t, 3); },
      "ease-in":   function(t) { return t * t * t; },
      "ease-in-out": function(t) {
        return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      }
    };

    function cancelAnimation() {
      if (anim) {
        if (anim.rafId) {
          try { window.cancelAnimationFrame(anim.rafId); } catch (_) {}
        }
        anim = null;
      }
    }

    function animateTo(targetTx, targetTy, targetS, opts) {
      opts = opts || {};
      cancelAnimation();
      var duration = (typeof opts.duration === "number" && opts.duration > 0) ? opts.duration : 200;
      var easeFn = easings[opts.easing] || easings["ease-out"];
      var startTx = tx, startTy = ty, startS = s;
      var clampedTargetS = clamp(targetS, sMin, sMax);
      var t0 = (typeof performance !== "undefined" && performance.now) ? performance.now() : Date.now();

      anim = { rafId: null };

      function step() {
        if (!anim) return;
        var now = (typeof performance !== "undefined" && performance.now) ? performance.now() : Date.now();
        var t = Math.min(1, (now - t0) / duration);
        var k = easeFn(t);
        tx = startTx + (targetTx - startTx) * k;
        ty = startTy + (targetTy - startTy) * k;
        s = startS + (clampedTargetS - startS) * k;
        // Don't clampPan during animation — clamp would yank an in-flight
        // glide if the target lands inside the bounds but interpolation
        // briefly steps outside.
        apply();
        if (t >= 1) {
          // Final state: apply the engine's full clamp + zoom-event so
          // the rest pos is canonical.
          tx = targetTx; ty = targetTy; s = clampedTargetS;
          clampPan();
          apply();
          emitZoom();
          anim = null;
          return;
        }
        anim.rafId = window.requestAnimationFrame(step);
      }
      anim.rafId = window.requestAnimationFrame(step);
    }

    function apply() {
      applyChildren(s);
      // At exactly 100%, land the picture on whole screen pixels: centring
      // can leave it half a pixel off, and the browser would then smear
      // every pixel across two — the opposite of what 100% is for. Only
      // the painted position snaps; tx/ty stay exact.
      var ptx = tx, pty = ty;
      if (actualSize && atScale(sActual())) {
        var d = 1 / sActual();
        ptx = Math.round(tx * d) / d;
        pty = Math.round(ty * d) / d;
      }
      // `translate3d(tx, ty, 0) rotate(rot deg)` — CSS reads right-
      // to-left, so rotate happens first (around the stage's own
      // 0,0 origin) then translate moves the rotated content into
      // place. Matches the image-pixel math in `rotatedContentBBox`
      // / `imageToScreen`.
      stage.style.transform =
        "translate3d(" + ptx + "px, " + pty + "px, 0) rotate(" + rot + "deg)";
      showZoom();
      syncGrid();
      bus._emit("animation", { tx: tx, ty: ty, scale: s, rotation: rot });
      bus._emit("update-viewport", { tx: tx, ty: ty, scale: s, rotation: rot });
    }

    function requestFrame() {
      if (frameRequested) return;
      frameRequested = true;
      window.requestAnimationFrame(function() {
        frameRequested = false;
        apply();
      });
    }

    // ── Gestures ───────────────────────────────────────────────────────────
    function viewportRect() { return el.getBoundingClientRect(); }
    function midpoint(p1, p2) { return { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 }; }
    function distance(p1, p2) {
      var dx = p2.x - p1.x, dy = p2.y - p1.y;
      return Math.sqrt(dx * dx + dy * dy);
    }

    function isFromNav(e) {
      return e.target && e.target.closest && (
        e.target.closest(".fresco-nav") ||
        e.target.closest("[data-fresco-no-capture]")
      );
    }

    // Same question for a middle-button press, which only `.fresco-nav`
    // blocks.
    //
    // `data-fresco-no-capture` is a peer overlay saying "I handle pointer
    // input in here" — and overlays claim the LEFT button: drawing, handles,
    // selection. None of them has a use for the middle button, so honouring
    // the opt-out for it would mean middle-drag panned over blank canvas but
    // died the moment it began on top of an annotation, which reads as the
    // gesture being broken. An overlay that does want the middle button can
    // still take it by calling `stopPropagation` — this listener is on the
    // container, in the bubble phase.
    //
    // `.fresco-nav` is Fresco's own chrome and keeps blocking: middle-
    // dragging off a zoom button should do nothing.
    function blocksMiddleDrag(e) {
      return e.target && e.target.closest && e.target.closest(".fresco-nav");
    }

    function snapshotGesture() {
      var rect = viewportRect();
      var pts = Array.from(pointers.values());
      if (pts.length === 1) {
        gestureStart = {
          kind: "pan",
          tx: tx, ty: ty,
          x: pts[0].x, y: pts[0].y,
          // `moved` flips true on the first pointermove beyond a 5px
          // threshold. Used to decide whether pointerup fires a "tap"
          // event (no movement) or just ends the pan gesture.
          moved: false,
          // Last-known client coords so onPointerUp can emit the tap
          // location even after release.
          lastClientX: pts[0].x,
          lastClientY: pts[0].y,
          // pointerType for the eventual tap payload.
          pointerType: "mouse"
        };
      } else if (pts.length >= 2) {
        var mid = midpoint(pts[0], pts[1]);
        gestureStart = {
          kind: "pinch",
          tx: tx, ty: ty, s: s,
          midX: mid.x - rect.left,
          midY: mid.y - rect.top,
          pageMidX: mid.x,
          pageMidY: mid.y,
          dist: distance(pts[0], pts[1])
        };
      } else {
        gestureStart = null;
      }
    }

    function onPointerDown(e) {
      // Left drags and middle drags both pan. Middle-drag exists so the
      // canvas can be panned without giving up whatever the left button is
      // currently for — an overlay that has taken it for drawing or for a
      // marquee selection leaves the user no way to move around otherwise.
      var isMouse = e.pointerType === "mouse";
      if (isMouse && e.button !== 0 && e.button !== MIDDLE_BUTTON) return;
      var middle = isMouse && e.button === MIDDLE_BUTTON;

      // An overlay that claims pointer input claims ONE pointer: a finger
      // drawing a line, a cursor dragging a handle. Two fingers are a pinch,
      // and that gesture is the canvas's whatever the overlay is doing with
      // the first one — the same reasoning that already exempts the middle
      // drag. Without this, moving around a board on a phone means putting
      // the pen down, switching to the pan tool, panning, and switching
      // back; and a second finger landing on a drawing overlay had nothing
      // to pinch WITH, so it fed the stroke instead.
      //
      // The claimed first finger is still COUNTED, so the second one has
      // something to pinch against. It starts no gesture of its own:
      // `gestureStart` stays null, `onPointerMove` only updates its
      // position, and `onPointerUp` clears it like any other.
      if (middle ? blocksMiddleDrag(e) : isFromNav(e)) {
        if (e.pointerType !== "touch") return;
        if (pointers.size === 0) {
          pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
          // Claimed by an overlay (a pen drawing a stroke): not a swipe.
          swipe = null;
          return;
        }
        // A second finger: this one is ours.
      }
      cancelAnimation();
      // Also stops the browser's middle-click autoscroll from kicking in
      // and fighting the drag.
      e.preventDefault();
      panButton = isMouse ? e.button : 0;
      try { el.setPointerCapture(e.pointerId); } catch (_) {}
      if (pointers.size === 0) {
        swipe = isMouse ? null : {
          x: e.clientX, y: e.clientY, t: Date.now(), atFit: s <= sHome * DBL_ZOOMED
        };
      } else {
        swipe = null;   // a second finger: a pinch, not a swipe
      }
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      el.classList.add("fresco--dragging");
      snapshotGesture();
      // Stash pointerType on the gesture snapshot so tap can report it.
      if (gestureStart && gestureStart.kind === "pan") {
        gestureStart.pointerType = e.pointerType || "mouse";
      }
    }

    function onPointerMove(e) {
      if (!pointers.has(e.pointerId)) return;
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (!gestureStart) return;

      if (gestureStart.kind === "pan") {
        // Track tap-vs-drag: any movement past 5px disqualifies the
        // upcoming pointerup from firing a "tap" event.
        if (!gestureStart.moved) {
          var ddx = e.clientX - gestureStart.x;
          var ddy = e.clientY - gestureStart.y;
          if (ddx * ddx + ddy * ddy > 25) gestureStart.moved = true;
        }
        gestureStart.lastClientX = e.clientX;
        gestureStart.lastClientY = e.clientY;
        // panLocked suppresses single-pointer drag entirely. Two-pointer
        // pinch (handled below) still works for zoom. setZoomFloor /
        // setZoomCeiling are honored implicitly via zoomAt's clamp.
        //
        // A middle drag is exempt, for the same reason pinch is: the lock
        // exists to free the LEFT drag for something else (a marquee
        // select, a drawing tool), not to forbid panning outright. A
        // consumer that locks pan at fit-scale is unaffected either way —
        // `clampPan` leaves nowhere to go at that scale.
        if (panLocked && panButton !== MIDDLE_BUTTON) return;
        if (!gestureEnabled("pan")) return;
        var dx = e.clientX - gestureStart.x;
        var dy = e.clientY - gestureStart.y;
        tx = gestureStart.tx + dx;
        ty = gestureStart.ty + dy;
        clampPan();
        bus._emit("pan", { tx: tx, ty: ty });
        requestFrame();
        return;
      }

      if (gestureStart.kind === "pinch") {
        if (!gestureEnabled("pinch")) return;
        var pts = Array.from(pointers.values());
        if (pts.length < 2) return;
        var newDist = distance(pts[0], pts[1]);
        if (newDist === 0) return;
        var newMid = midpoint(pts[0], pts[1]);

        var s2 = clamp(gestureStart.s * (newDist / gestureStart.dist), sMin, sMax);
        var kEff = s2 / gestureStart.s;
        var newTx = gestureStart.midX - (gestureStart.midX - gestureStart.tx) * kEff;
        var newTy = gestureStart.midY - (gestureStart.midY - gestureStart.ty) * kEff;

        newTx += (newMid.x - gestureStart.pageMidX);
        newTy += (newMid.y - gestureStart.pageMidY);

        tx = newTx; ty = newTy; s = s2;
        clampPan();
        emitZoom();
        bus._emit("pan", { tx: tx, ty: ty });
        requestFrame();
      }
    }

    // A one-finger swipe across a picture that is fully zoomed out: the
    // host's cue to show the next or previous one (`swipe` on the bus, and a
    // bubbling `fresco:swipe` DOM event for a host that would rather listen
    // on an ancestor that outlives the viewer). Only at the fitted view,
    // where a drag has nothing to pan — zoomed in, the same gesture is a pan
    // and must stay one — and only for a deliberate flick: far enough,
    // mostly sideways, quick. Touch and pen only; a mouse drag is never
    // read as "next".
    function endSwipe(e) {
      var sw = swipe;
      if (!sw || !sw.atFit || s > sHome * DBL_ZOOMED) return;
      if (!gestureEnabled("swipe")) return;
      var dx = e.clientX - sw.x;
      var dy = e.clientY - sw.y;
      if (Math.abs(dx) < SWIPE_MIN_PX) return;
      if (Math.abs(dx) < Math.abs(dy) * SWIPE_RATIO) return;
      if (Date.now() - sw.t > SWIPE_MAX_MS) return;
      var detail = { direction: dx < 0 ? "left" : "right" };
      bus._emit("swipe", detail);
      if (typeof CustomEvent === "function") {
        el.dispatchEvent(new CustomEvent("fresco:swipe", { bubbles: true, detail: detail }));
      }
    }

    function onPointerUp(e) {
      // Capture tap snapshot before mutating pointer state. Tap fires
      // only when the gesture was single-pointer and stayed below the
      // 5px movement threshold throughout. Touch and pen taps also
      // qualify — useful for swipe-paged readers that need tap-to-turn
      // semantics without re-rolling drag-vs-tap detection.
      // A middle click is never a tap. Consumers wire `tap` to content
      // actions (page turns, opening a link), and a stray middle press
      // that happened not to travel far enough shouldn't trigger those.
      var tapCandidate = (
        pointers.size === 1 && panButton !== MIDDLE_BUTTON &&
        gestureStart && gestureStart.kind === "pan" && !gestureStart.moved
      ) ? gestureStart : null;

      pointers.delete(e.pointerId);
      try { el.releasePointerCapture(e.pointerId); } catch (_) {}
      if (pointers.size >= 1) {
        snapshotGesture();
        return;
      }
      gestureStart = null;
      panButton = 0;
      el.classList.remove("fresco--dragging");
      if (e.type !== "pointercancel") endSwipe(e);
      swipe = null;

      if (tapCandidate && e.type !== "pointercancel") {
        var rect = viewportRect();
        var localX = tapCandidate.lastClientX - rect.left;
        var localY = tapCandidate.lastClientY - rect.top;
        // Two suppression paths — either lets a consumer or peer
        // library (Etcher annotations, custom overlays) opt out of
        // the tap emit without forking fresco:
        //
        //   1. `suppressNextTap(ms?)` deadline. Useful when the
        //      caller knows it just emitted a synthetic gesture
        //      and wants to swallow the iOS-synthesized mousedown/
        //      mouseup that follows.
        //
        //   2. `[data-fresco-suppress-tap]` on any element whose
        //      bbox contains the tap point. Useful for static
        //      surfaces — Etcher stamps it on every `.etcher-shape`
        //      so tapping a pinned annotation never bubbles to the
        //      consumer's tap-zone navigation. We walk
        //      `document.querySelectorAll("[data-fresco-suppress-tap]")`
        //      and bbox-test each rather than relying on
        //      `elementsFromPoint`, because the latter honors
        //      `pointer-events: none` (per spec, verified across
        //      browsers) — and Etcher applies `pointer-events: none`
        //      to every non-editing `.etcher-shape` so pan/zoom
        //      passes through. The probe used to silently miss
        //      every shape; the bbox walk catches them regardless
        //      of CSS pointer-events.
        var now = Date.now();
        if (suppressTapUntil > now) {
          return;
        }
        try {
          var tapX = tapCandidate.lastClientX;
          var tapY = tapCandidate.lastClientY;
          var nodes = document.querySelectorAll("[data-fresco-suppress-tap]");
          for (var ni = 0; ni < nodes.length; ni++) {
            var n = nodes[ni];
            if (!n || !n.getBoundingClientRect) continue;
            var bbox = n.getBoundingClientRect();
            if (bbox.width === 0 && bbox.height === 0) continue;
            if (
              tapX >= bbox.left && tapX <= bbox.right &&
              tapY >= bbox.top  && tapY <= bbox.bottom
            ) {
              return;
            }
          }
        } catch (_) { /* defensive — never let probe errors swallow the tap */ }
        bus._emit("tap", {
          x: localX,
          y: localY,
          imageX: (localX - tx) / s,
          imageY: (localY - ty) / s,
          pointerType: tapCandidate.pointerType || e.pointerType || "mouse"
        });
      }
    }

    // Cancels the browser's native drag so panning across an image doesn't
    // start dragging the image itself — the ghost thumbnail follows the
    // cursor and the pan dies under it.
    //
    // Overlays are exempt, on the same opt-out as every other gesture here.
    // Without that, nothing layered on the canvas can use drag-and-drop at
    // all: a reorderable list inside an overlay gets as far as the pointer
    // going down and then never moves, because `dragstart` is cancelled
    // before the browser can begin. The opt-out exists to say "I handle
    // pointer input in here", and this is pointer input.
    function onDragStart(e) {
      if (isFromNav(e)) return;
      e.preventDefault();
    }

    // ── Wheel, fingers, pinch: one event, three intentions ───────────────
    //
    // A notch of a wheel has meant zoom here since the first version. Two
    // fingers pushing a trackpad mean move the picture, and a pinch means
    // zoom — and all three arrive as `wheel`, with nothing in the event
    // naming the device that sent it.
    //
    // So they are told apart by SHAPE. A wheel steps in whole, uniform
    // notches (100 or 120 px, or whole lines, which is `deltaMode` 1) and
    // never sideways. Fingers send small deltas, often fractional, with a
    // sideways component the moment the push is not perfectly straight.
    //
    // Both readings are guesses, so the answer is LATCHED for the burst:
    // one finger-shaped event makes the rest of that flick fingers too.
    // Without it a pan turns into a zoom halfway through, when momentum
    // deltas grow past the threshold or land on round numbers — which is
    // worse than either gesture being wrong outright, because it happens
    // mid-movement.
    //
    // A tilt wheel reads as fingers, and should: sideways on a mouse means
    // move the picture sideways too. Anyone whose mouse lands on the wrong
    // side of this can still zoom with ctrl+wheel, the nav buttons, +/-,
    // or a double-click.
    var TRACKPAD_STEP_MAX = 40;    // px a finger push stays under early in a flick
    var TRACKPAD_BURST_MS = 400;   // quiet for this long and the next event is judged afresh
    var burstKind = null;          // "fingers" | "wheel" — who owns the flick in progress
    var WHEEL_RATE = 0.0015;       // zoom per px of wheel
    // How far past the fitted scale counts as "zoomed in" for the double-click
    // toggle: a hair of slack, so a view a wheel nudged by a pixel still
    // reads as fitted and zooms in rather than snapping home.
    var DBL_ZOOMED = 1.02;
    // What counts as a swipe (see endSwipe): this far sideways, this much
    // more sideways than up or down, inside this long.
    var SWIPE_MIN_PX = 50;
    var SWIPE_RATIO = 1.5;
    var SWIPE_MAX_MS = 800;

    // Wheels do not all speak in pixels, and everything here is priced in
    // them. A wheel that reports LINES — `deltaMode` 1, which plenty of
    // mice and Firefox do — sends 6 for the notch that a pixel-reporting
    // one sends 120 for, so the same notch bought a twentieth of the zoom
    // and moved the picture six pixels. It read as a viewer that would
    // not zoom and could not be scrolled anywhere, and no amount of
    // reading the device better was going to fix a unit.
    //
    // The legacy `wheelDelta` is the browser's own pixel currency — 120 a
    // notch, whatever the mode — so it converts line deltas exactly where
    // it is there, and a line is worth 40px where it is not (the number
    // Firefox itself uses: 3 lines to a 120px notch).
    var LINE_PX = 40;
    var PAGE_PX = 800;

    function wheelPxY(e) {
      if (e.deltaMode === 0) return e.deltaY;
      if (e.deltaMode === 2) return e.deltaY * PAGE_PX;
      var legacy = typeof e.wheelDeltaY === "number" ? e.wheelDeltaY
                 : (typeof e.wheelDelta === "number" ? e.wheelDelta : 0);
      // Only when it is really in that currency: an event built by script
      // carries the line count itself there, and taking a 6 at face value
      // is the very mistake this function exists to undo. A notch is 120;
      // anything under a single line's worth is not pixels.
      if (Math.abs(legacy) >= LINE_PX && e.deltaY !== 0) {
        return Math.abs(legacy) * (e.deltaY > 0 ? 1 : -1);
      }
      return e.deltaY * LINE_PX;
    }

    function wheelPxX(e) {
      if (e.deltaMode === 0) return e.deltaX;
      return e.deltaX * (e.deltaMode === 2 ? PAGE_PX : LINE_PX);
    }
    // Zoom per px of pinch. Fingers move a picture a few px at a time, so
    // this is the dial that decides whether a pinch crosses a zoom level
    // or has to be repeated until it does — tuned by hand against real
    // hardware, like any gain.
    var PINCH_RATE = 0.035;
    var trackpadAt = 0;            // when a finger-shaped event was last seen

    function wheelIsFingers(e) {
      // Whole lines or pages: a wheel, and one whose delta is not in px.
      if (e.deltaMode !== 0) return false;

      var now = Date.now();

      // A burst belongs to whoever claimed it, either way round. One
      // notch of a mouse does not arrive as one event on a Mac: the
      // system smooths it into a run of small deltas that are shaped like
      // fingers, and only the first of them carries the notch. Judging
      // each event on its own meant the first zoomed and the rest panned
      // — a notch worth of scrolling buying a couple of percent of zoom,
      // which is what "the wheel got slow" was. Whichever device speaks
      // first holds the flick until it ends.
      if (burstKind !== null && now - trackpadAt < TRACKPAD_BURST_MS) {
        trackpadAt = now;
        return burstKind === "fingers";
      }

      // Size alone is not enough, and on a Mac it is barely a clue: the
      // system hands a MOUSE wheel to the browser as a run of small,
      // momentum-shaped deltas, exactly like a trackpad's. Read that way,
      // most of a wheel's notches became pans — invisible at fit scale,
      // where there is nowhere to pan — and the few that got through left
      // zooming feeling broken rather than reassigned.
      //
      // The legacy `wheelDeltaY` is what tells them apart there. A wheel
      // reports whole notches, which the engine mirrors as multiples of
      // 120 whatever the px delta; fingers report three times their pixels
      // and land on 120 only by coincidence — which is why this is trusted
      // only OUTSIDE a flick, where one coincidental event cannot turn a
      // pan into a zoom mid-movement. Browsers that do not carry the
      // property at all (it is legacy, and Firefox has never had it) say
      // nothing here and fall through to shape, as before.
      var legacy = typeof e.wheelDeltaY === "number" ? e.wheelDeltaY
                 : (typeof e.wheelDelta === "number" ? e.wheelDelta : 0);
      var notch = legacy !== 0 && Math.abs(legacy) % 120 === 0 && e.deltaX === 0;

      var fingers = !notch &&
        (e.deltaX !== 0 ||
         e.deltaY !== Math.round(e.deltaY) ||
         (e.deltaY !== 0 && Math.abs(e.deltaY) < TRACKPAD_STEP_MAX));

      burstKind = fingers ? "fingers" : "wheel";
      trackpadAt = now;
      return fingers;
    }

    function onWheel(e) {
      if (isFromNav(e)) return;
      if (!gestureEnabled("wheel")) return;
      cancelAnimation();
      e.preventDefault();
      var rect = viewportRect();
      var px = e.clientX - rect.left;
      var py = e.clientY - rect.top;

      // A pinch on a trackpad arrives as a wheel with `ctrlKey` set, with
      // no key held — every browser reports it that way, and it is the
      // only signal there is. Ctrl+wheel on a mouse is the same gesture by
      // convention, so both land here, and so does ⌘+wheel: on a laptop
      // two fingers are spoken for by the pan now, and holding a key is a
      // steadier way to zoom than pinching. Its deltas are an order of
      // magnitude smaller than a notch, hence the steeper rate: at the
      // wheel's own rate a pinch across the whole trackpad barely moves
      // the picture.
      if (e.ctrlKey || e.metaKey) {
        // Fingers and a wheel are as far apart here as they are below: a
        // pinch measures a few px an event, a notch a hundred. One rate
        // for both makes one of them useless — a pinch you have to repeat
        // a dozen times to cross a zoom level, or a wheel that jumps two
        // per click. So each keeps its own.
        var pinchPx = wheelPxY(e);
        if (pinchPx) {
          zoomAt(px, py, Math.exp(-pinchPx * (wheelIsFingers(e) ? PINCH_RATE : WHEEL_RATE)));
        }
        return;
      }

      // Two fingers moving the picture. Gated on `pan` as well as `wheel`:
      // a host that turned panning off means it, whichever device asks.
      //
      // The sign is a page's: the deltas say how far a page would scroll,
      // and the picture moves the way that page's content would — against
      // the delta. On a trackpad that scrolls naturally (every Mac out of
      // the box) that is with the fingers, the picture held and dragged
      // along like a photo on a table. 0.13 passed the deltas straight
      // through, so the picture went the opposite way to a page's content
      // under the same fingers, and everyone had to flip it to get what
      // the rest of their machine does.
      if (wheelIsFingers(e)) {
        if (gestureEnabled("pan")) {
          var dir = twoFingerPanInverted ? 1 : -1;
          panRaw(dir * wheelPxX(e), dir * wheelPxY(e));
        }
        return;
      }

      zoomAt(px, py, Math.exp(-wheelPxY(e) * WHEEL_RATE));
    }

    // A toggle, the way photo viewers do it: from the fitted view a double
    // click (or a double tap — phones send one) zooms in on that spot; from
    // anywhere zoomed in, it goes back to the fitted view. It used to zoom in
    // 2× every time, so tapping the same spot twice more to get back out
    // only went further in, until the ceiling. Back means zoom and pan only:
    // a rotation the user chose stays (that is what "reset view" is for).
    function onDblClick(e) {
      if (isFromNav(e)) return;
      if (!gestureEnabled("double_click")) return;
      cancelAnimation();
      if (s > sHome * DBL_ZOOMED) {
        requestHome();
        return;
      }
      var rect = viewportRect();
      zoomAt(e.clientX - rect.left, e.clientY - rect.top, 2);
    }

    function onKeyDown(e) {
      var t = e.target;
      if (t && t !== el && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      if (!gestureEnabled("keyboard")) return;
      var handled = true;
      switch (e.key) {
        case "ArrowUp":    panBy(0, 60);  break;
        case "ArrowDown":  panBy(0, -60); break;
        case "ArrowLeft":  panBy(60, 0);  break;
        case "ArrowRight": panBy(-60, 0); break;
        case "+": case "=": zoomAt(vw / 2, vh / 2, 1.4); break;
        case "-": case "_": zoomAt(vw / 2, vh / 2, 1 / 1.4); break;
        case "0": resetView(); break;
        case "1":
          if (!actualSize) { handled = false; break; }
          toggleActualSize();
          break;
        case "f": case "F": toggleFullscreen(); break;
        default: handled = false;
      }
      if (handled) e.preventDefault();
    }

    function toggleFullscreen() {
      if (document.fullscreenElement === el) {
        if (document.exitFullscreen) document.exitFullscreen();
      } else if (el.requestFullscreen) {
        el.requestFullscreen().catch(function() {});
      }
    }

    // Convenience zoom helpers that mirror the nav buttons' behavior
    // exactly — same step factor (1.4×), same anchor (viewport
    // center). Consumers wiring custom toolbars / keyboard shortcuts
    // / accessibility affordances call these instead of replicating
    // the math against `zoomAt` and `viewportRect`.
    function zoomIn(factor) {
      var rect = viewportRect();
      vw = rect.width; vh = rect.height;
      zoomAt(vw / 2, vh / 2, factor || 1.4);
    }
    function zoomOut(factor) {
      var rect = viewportRect();
      vw = rect.width; vh = rect.height;
      zoomAt(vw / 2, vh / 2, 1 / (factor || 1.4));
    }

    // ── Actual size ────────────────────────────────────────────────────────
    // Within 1% counts as there: a pinch that lands at 99.6% is at 100% as
    // far as the button is concerned.
    function atScale(target) { return Math.abs(s / target - 1) < 0.01; }

    function zoomPercent() { return (s / sActual()) * 100; }

    // To 100%, about the middle of the viewer.
    function zoomToActualSize() {
      cancelAnimation();
      refresh();
      zoomAt(vw / 2, vh / 2, sActual() / s);
    }

    // The nav's magnifier-with-a-1: to 100%, and from 100% to fit. For a
    // big picture that is home and back; for one smaller than the viewer,
    // home already IS 100%, so the button is how it fills the view.
    function toggleActualSize() {
      cancelAnimation();
      refresh();
      if (!atScale(sActual())) {
        zoomToActualSize();
        return;
      }
      if (atScale(sHome)) fitToView();
      else requestHome();
      emitZoom();
    }

    function emitZoom() { bus._emit("zoom", { scale: s, percent: zoomPercent() }); }

    // The zoom control (`actual_size`): the percentage, always showing in
    // the bottom-right corner, and a slider that opens beside it — on hover
    // with a mouse, on a tap or click otherwise. The slider has detents at
    // ZOOM_DETENTS: they sit evenly along it whatever lies between them, so
    // the stretch from 200% to 400% at the far end runs fast, and the thumb
    // sticks at each one for a few pixels of travel before moving on.
    // Fed from apply(), so every path that changes the scale (wheel, pinch,
    // buttons, a host's setTransform, a resize) keeps it current.
    var ZOOM_DETENTS = [50, 100, 150, 200, 400];
    var ZOOM_TOP = 400;
    var DETENT_PX = 7;
    var zoomCtl = null, zoomPct = null, zoomTrack = null, zoomThumb = null, zoomTicks = null;
    var pillScale = null, zoomOpenTimer = null, zoomDrag = null, zoomDetent = null, zoomStopsKey = "";
    var zoomOffDoc = null;
    if (actualSize && typeof document !== "undefined") buildZoomControl();

    function buildZoomControl() {
      zoomCtl = document.createElement("div");
      zoomCtl.className = "fresco-zoom";
      zoomCtl.setAttribute("data-fresco-no-capture", "");
      zoomTrack = document.createElement("div");
      zoomTrack.className = "fresco-zoom-track";
      zoomTrack.setAttribute("role", "slider");
      zoomTrack.setAttribute("aria-label", "Zoom");
      zoomTrack.tabIndex = -1;
      var rail = document.createElement("div");
      rail.className = "fresco-zoom-rail";
      zoomTicks = document.createElement("div");
      zoomThumb = document.createElement("div");
      zoomThumb.className = "fresco-zoom-thumb";
      zoomTrack.appendChild(rail);
      zoomTrack.appendChild(zoomTicks);
      zoomTrack.appendChild(zoomThumb);
      zoomPct = document.createElement("button");
      zoomPct.type = "button";
      zoomPct.className = "fresco-zoom-pct";
      zoomPct.title = "Fit to screen";
      zoomPct.setAttribute("aria-expanded", "false");
      zoomCtl.appendChild(zoomTrack);
      zoomCtl.appendChild(zoomPct);
      el.appendChild(zoomCtl);

      zoomCtl.addEventListener("pointerenter", function(e) {
        if (e.pointerType === "mouse") setZoomOpen(true);
      });
      zoomCtl.addEventListener("pointerleave", function(e) {
        if (e.pointerType === "mouse" && !zoomDrag) closeZoomSoon();
      });
      // Not every browser says on a click which pointer made it.
      var pressedWith = null;
      zoomPct.addEventListener("pointerdown", function(e) { pressedWith = e.pointerType; });
      zoomPct.addEventListener("click", function(e) {
        e.stopPropagation();
        // The percentage is also a way back: a click fits the picture to the
        // view (what the 1:1 button does on its second press), and a second
        // click returns to home where that is not the fit — a small picture's
        // 100%. The slider stays open (a mouse opened it by hovering; a tap
        // opens it here), so the zoom can be set from there.
        setZoomOpen(true);
        pressedWith = null;
        cancelAnimation();
        refresh();
        if (atScale(sFit) && !atScale(sHome)) requestHome();
        else fitToView();
        emitZoom();
      });
      zoomCtl.addEventListener("dblclick", function(e) { e.stopPropagation(); });
      zoomCtl.addEventListener("wheel", function(e) { e.stopPropagation(); }, { passive: true });
      zoomTrack.addEventListener("pointerdown", onZoomTrackDown);
      zoomTrack.addEventListener("pointermove", onZoomTrackMove);
      zoomTrack.addEventListener("pointerup", onZoomTrackUp);
      zoomTrack.addEventListener("pointercancel", onZoomTrackUp);
      zoomTrack.addEventListener("keydown", onZoomTrackKey);
      zoomCtl.addEventListener("keydown", function(e) {
        if (e.key === "Escape") { setZoomOpen(false); zoomPct.focus(); }
        e.stopPropagation();
      });
      // A tap anywhere else closes it.
      zoomOffDoc = function(e) {
        if (zoomCtl.hasAttribute("data-open") && !zoomCtl.contains(e.target)) setZoomOpen(false);
      };
      document.addEventListener("pointerdown", zoomOffDoc, true);
    }

    function setZoomOpen(open) {
      if (!zoomCtl) return;
      if (zoomOpenTimer) { clearTimeout(zoomOpenTimer); zoomOpenTimer = null; }
      if (open === zoomCtl.hasAttribute("data-open")) return;
      if (open) {
        zoomCtl.setAttribute("data-open", "");
        zoomTrack.tabIndex = 0;
        pillScale = null;
        showZoom();
      } else {
        zoomCtl.removeAttribute("data-open");
        zoomTrack.tabIndex = -1;
      }
      zoomPct.setAttribute("aria-expanded", open ? "true" : "false");
    }

    function closeZoomSoon() {
      if (zoomOpenTimer) clearTimeout(zoomOpenTimer);
      zoomOpenTimer = setTimeout(function() { zoomOpenTimer = null; setZoomOpen(false); }, 500);
    }

    // The percentages the slider runs through, low end to high: the zoom-out
    // floor, every detent above it, and ZOOM_TOP (or the ceiling, if lower).
    // A detent within 1% of the floor IS the floor — a small picture opening
    // at 100% starts its slider on the 100% detent.
    function zoomStops() {
      var lo = (sMin / sActual()) * 100;
      var hi = Math.min(ZOOM_TOP, (sMax / sActual()) * 100);
      if (!(hi > lo * 1.01)) return null;
      var stops = [{ p: lo, detent: false }];
      for (var i = 0; i < ZOOM_DETENTS.length; i++) {
        var d = ZOOM_DETENTS[i];
        if (Math.abs(d / lo - 1) < 0.01) { stops[0] = { p: d, detent: true }; continue; }
        if (d > lo && d <= hi * 1.0001) stops.push({ p: d, detent: true });
      }
      if (stops[stops.length - 1].p < hi / 1.0001) stops.push({ p: hi, detent: false });
      return stops;
    }

    // Slider position (0..1) for a percentage, and back. The stops are
    // spaced evenly; between two of them the zoom runs geometrically, so a
    // drag feels the same at 20% as at 300%.
    function zoomToPos(p, stops) {
      var n = stops.length - 1;
      if (p <= stops[0].p) return 0;
      for (var i = 0; i < n; i++) {
        var a = stops[i].p, b = stops[i + 1].p;
        if (p <= b) return (i + Math.log(p / a) / Math.log(b / a)) / n;
      }
      return 1;
    }

    function posToZoom(t, stops) {
      var n = stops.length - 1;
      var x = clamp(t, 0, 1) * n;
      var i = Math.min(n - 1, Math.floor(x));
      var a = stops[i].p, b = stops[i + 1].p;
      return a * Math.pow(b / a, x - i);
    }

    // Where a press at `x` CSS pixels along the track lands: on a detent if
    // it is within DETENT_PX of one, otherwise exactly where it is.
    function zoomAtTrackX(x, width, stops) {
      for (var i = 0; i < stops.length; i++) {
        if (!stops[i].detent) continue;
        if (Math.abs(x - zoomToPos(stops[i].p, stops) * width) <= DETENT_PX) {
          return { p: stops[i].p, detent: stops[i].p };
        }
      }
      return { p: posToZoom(x / width, stops), detent: null };
    }

    function zoomToPercent(p) {
      cancelAnimation();
      refresh();
      zoomAt(vw / 2, vh / 2, (p / 100) * sActual() / s);
    }

    function onZoomTrackDown(e) {
      if (!zoomCtl.hasAttribute("data-open")) return;
      e.preventDefault();
      e.stopPropagation();
      try { zoomTrack.setPointerCapture(e.pointerId); } catch (_) {}
      zoomDrag = { id: e.pointerId };
      zoomDetent = null;
      zoomTrack.focus({ preventScroll: true });
      onZoomTrackMove(e);
    }

    function onZoomTrackMove(e) {
      if (!zoomDrag || e.pointerId !== zoomDrag.id) return;
      var stops = zoomStops();
      if (!stops) return;
      var r = zoomTrack.getBoundingClientRect();
      var hit = zoomAtTrackX(e.clientX - r.left, r.width, stops);
      if (hit.detent !== null && hit.detent !== zoomDetent) {
        // The "chick": a buzz where the device has one.
        try { if (navigator.vibrate) navigator.vibrate(6); } catch (_) {}
      }
      zoomDetent = hit.detent;
      zoomToPercent(hit.p);
    }

    function onZoomTrackUp(e) {
      if (!zoomDrag || e.pointerId !== zoomDrag.id) return;
      zoomDrag = null;
      zoomDetent = null;
      try { zoomTrack.releasePointerCapture(e.pointerId); } catch (_) {}
      if (e.pointerType === "mouse" && !zoomCtl.matches(":hover")) closeZoomSoon();
    }

    // Arrows nudge; Page Up / Page Down go detent to detent.
    function onZoomTrackKey(e) {
      var stops = zoomStops();
      if (!stops) return;
      var t = zoomToPos(zoomPercent(), stops);
      var p = null;
      if (e.key === "ArrowRight" || e.key === "ArrowUp") p = posToZoom(t + 0.025, stops);
      else if (e.key === "ArrowLeft" || e.key === "ArrowDown") p = posToZoom(t - 0.025, stops);
      else if (e.key === "Home") p = stops[0].p;
      else if (e.key === "End") p = stops[stops.length - 1].p;
      else if (e.key === "PageUp" || e.key === "PageDown") {
        var up = e.key === "PageUp", cur = zoomPercent();
        for (var i = up ? 0 : stops.length - 1; up ? i < stops.length : i >= 0; i += up ? 1 : -1) {
          if (up ? stops[i].p > cur * 1.01 : stops[i].p < cur / 1.01) { p = stops[i].p; break; }
        }
      }
      if (p === null) return;
      e.preventDefault();
      zoomToPercent(p);
    }

    function formatPercent(p) {
      if (p >= 10) return Math.round(p) + "%";
      var one = p.toFixed(1);
      if (one === "0.0") return "<0.1%";
      return one.replace(/\.0$/, "") + "%";
    }

    function showZoom() {
      if (!zoomCtl || !ready || s === pillScale) return;
      pillScale = s;
      var pct = zoomPercent();
      zoomPct.textContent = formatPercent(pct);
      zoomTrack.setAttribute("aria-valuenow", String(Math.round(pct)));
      zoomTrack.setAttribute("aria-valuetext", formatPercent(pct));
      var stops = zoomStops();
      var key = stops ? stops.map(function(x) { return x.p.toFixed(2); }).join(",") : "";
      if (key !== zoomStopsKey) {
        zoomStopsKey = key;
        zoomTicks.textContent = "";
        if (stops) {
          zoomTrack.setAttribute("aria-valuemin", String(Math.round(stops[0].p)));
          zoomTrack.setAttribute("aria-valuemax", String(Math.round(stops[stops.length - 1].p)));
          for (var i = 0; i < stops.length; i++) {
            if (!stops[i].detent) continue;
            var tick = document.createElement("div");
            tick.className = "fresco-zoom-tick";
            tick.title = stops[i].p + "%";
            tick.style.left = (zoomToPos(stops[i].p, stops) * 100) + "%";
            zoomTicks.appendChild(tick);
          }
        }
      }
      zoomThumb.style.left = (stops ? zoomToPos(pct, stops) * 100 : 0) + "%";
      var onDetent = !!stops && stops.some(function(x) { return x.detent && Math.abs(pct / x.p - 1) < 0.001; });
      if (onDetent) zoomCtl.setAttribute("data-detent", "");
      else zoomCtl.removeAttribute("data-detent");
      syncActualButton();
    }

    // The button says where it will go: to 100%, or (at 100%) to fit.
    function syncActualButton() {
      var btn = navEl && navEl.querySelector && (navEl.querySelector('[data-fresco-nav="actual_size"]') ||
        (navEl.parentNode && navEl.parentNode.querySelector('.fresco-nav-overflow [data-fresco-nav="actual_size"]')));
      if (!btn) return;
      var at = atScale(sActual());
      var title = at ? "Fit to screen" : "Actual size (100%)";
      if (btn.title !== title) {
        btn.title = title;
        btn.setAttribute("aria-label", title);
      }
      btn.setAttribute("aria-pressed", at ? "true" : "false");
    }

    // ── Listeners + nav + resize ───────────────────────────────────────────
    el.addEventListener("pointerdown", onPointerDown);
    // Middle-click's own defaults, which would otherwise land at the end of
    // a middle drag: paste-on-primary-selection under X11, and open-in-new-
    // tab if the drag happens to finish over a link in an overlay.
    el.addEventListener("auxclick", function(e) {
      if (e.button === MIDDLE_BUTTON && !blocksMiddleDrag(e)) e.preventDefault();
    });
    el.addEventListener("pointermove", onPointerMove);
    el.addEventListener("pointerup", onPointerUp);
    el.addEventListener("pointercancel", onPointerUp);
    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("dblclick", onDblClick);
    el.addEventListener("keydown", onKeyDown);
    el.addEventListener("dragstart", onDragStart);
    el.addEventListener("pointermove", onKonamiPointer);
    el.addEventListener("pointerleave", onKonamiPointerLeave);
    // Capture, on the window: ahead of a host's own arrow-key handlers.
    window.addEventListener("keydown", onKonamiKey, true);

    var navEl = buildNav(el, {
      onFit: function() { resetView(); },
      onZoomIn: function() {
        var rect = viewportRect();
        vw = rect.width; vh = rect.height;
        zoomAt(vw / 2, vh / 2, 1.4);
      },
      onZoomOut: function() {
        var rect = viewportRect();
        vw = rect.width; vh = rect.height;
        zoomAt(vw / 2, vh / 2, 1 / 1.4);
      },
      onActualSize: actualSize ? toggleActualSize : null,
      onRotate: function() { rotateBy(90); },
      onRotateLeft: function() { rotateBy(-90); },
      onFullscreen: toggleFullscreen
    }, {
      navButtonEnabled: function(name) { return navButtonEnabled(name); },
      layout: opts.navLayout,
      overflow: opts.navOverflow,
      reverse: opts.navReverse
    });

    var resizeObserver = null;
    if (typeof ResizeObserver === "function") {
      resizeObserver = new ResizeObserver(function() {
        if (!ready) return;
        var rect = viewportRect();
        if (rect.width === vw && rect.height === vh) return;
        vw = rect.width; vh = rect.height;
        recomputeBounds();
        if (s < sMin) s = sMin;
        if (s > sMax) s = sMax;
        clampPan();
        bus._emit("resize", { width: vw, height: vh });
        requestFrame();
      });
      resizeObserver.observe(el);
    }

    function teardown() {
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerup", onPointerUp);
      el.removeEventListener("pointercancel", onPointerUp);
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("dblclick", onDblClick);
      el.removeEventListener("keydown", onKeyDown);
      el.removeEventListener("dragstart", onDragStart);
      el.removeEventListener("pointermove", onKonamiPointer);
      el.removeEventListener("pointerleave", onKonamiPointerLeave);
      window.removeEventListener("keydown", onKonamiKey, true);
      if (rippleFrame) { cancelAnimationFrame(rippleFrame); rippleFrame = null; }
      if (gridGuard) { gridGuard.disconnect(); gridGuard = null; }
      if (resizeObserver) {
        try { resizeObserver.disconnect(); } catch (_) {}
        resizeObserver = null;
      }
      if (navEl && navEl._frescoNav) navEl._frescoNav.teardown();
      if (navEl && navEl.parentNode) navEl.parentNode.removeChild(navEl);
      if (zoomOpenTimer) { clearTimeout(zoomOpenTimer); zoomOpenTimer = null; }
      if (zoomOffDoc) { document.removeEventListener("pointerdown", zoomOffDoc, true); zoomOffDoc = null; }
      if (zoomCtl && zoomCtl.parentNode) zoomCtl.parentNode.removeChild(zoomCtl);
    }

    // Override the engine's sMin (the zoom-out floor). Pass a positive
    // number to set; pass null/undefined/0 to revert to the engine
    // default (sFit for clamped mode, sFit*0.05 for infinite_canvas).
    // The new floor applies to all subsequent zoom paths (wheel, pinch,
    // double-click, fitBounds via setTransform) — consumers can't
    // accidentally bypass their own floor.
    function setZoomFloor(v) {
      customSMin = (typeof v === "number" && v > 0) ? v : null;
      recomputeBounds();
      if (s < sMin) {
        s = sMin;
        clampPan();
        emitZoom();
        requestFrame();
      }
    }

    // Symmetric ceiling override. Same semantics as setZoomFloor.
    function setZoomCeiling(v) {
      customSMax = (typeof v === "number" && v > 0) ? v : null;
      recomputeBounds();
      if (s > sMax) {
        s = sMax;
        clampPan();
        emitZoom();
        requestFrame();
      }
    }

    // When locked: panBy + single-pointer drag are no-ops; two-pointer
    // pinch still works for zoom. Consumers toggle this in response to
    // scale changes (e.g. paged readers lock pan at fit, unlock when
    // zoomed in).
    function setPanLocked(b) { panLocked = !!b; }

    // Flip two-finger pan for a machine whose trackpad scrolls the other
    // way to most (see `twoFingerPanInverted`). Takes effect on the next
    // wheel event, mid-flick included.
    function setTwoFingerPanInverted(b) { twoFingerPanInverted = !!b; }
    function getTwoFingerPanInverted() { return twoFingerPanInverted; }

    // Clamp pan to a custom rect (canvas-pixel coords). Overrides
    // infinite_canvas's no-clamp contract. Pass null to revert.
    // Triggers an immediate re-clamp + frame so the new constraint
    // applies on the next paint.
    function setPanBounds(rect) {
      customPanBounds = (rect && typeof rect.x === "number" &&
                                  typeof rect.y === "number" &&
                                  typeof rect.width === "number" &&
                                  typeof rect.height === "number") ? rect : null;
      clampPan();
      requestFrame();
    }

    // Override the "home" action (nav reset button + `0` key). The
    // engine's fit() primitive is unaffected; only the user-triggered
    // home affordances route through the override when set. Pass null
    // to revert.
    function setHomeAction(fn) {
      customHome = (typeof fn === "function") ? fn : null;
    }

    // Swallow every `tap` event for the next `ms` (default 250).
    // Useful for peer libraries that just emitted a gesture which
    // will be followed by an OS-synthesized tap they want to
    // suppress (mobile Safari fires synthesized mousedown/mouseup
    // after touchend; the resulting tap would race the library's
    // own state mutations). Calls are additive — re-calling
    // extends the deadline to the later of the two.
    function suppressNextTap(ms) {
      var window = (typeof ms === "number" && ms > 0) ? ms : 250;
      var deadline = Date.now() + window;
      if (deadline > suppressTapUntil) suppressTapUntil = deadline;
    }

    // Snap any input to {0, 90, 180, 270}. Re-homes so the new
    // rotation lands on a centered, on-screen view (otherwise a 90°
    // rotation at the previous tx/ty would push the content off the
    // side, since the content's rotated bbox is shaped differently).
    // Fires `rotate` once on actual changes — no-op when the snapped
    // input equals the current rotation.
    //
    // The re-home routes through `requestHome()` instead of bare
    // `fit()` so any `customHome` set via `setHomeAction` runs too —
    // a paged manga reader that fits the current page on reset
    // should re-fit the current page after rotating, not the whole
    // multi-image canvas. Consumers without a customHome get the
    // engine's default fit (`fit()`).
    // The background dot grid. Purely visual — nothing about the transform,
    // the content or what anyone else sees changes with it.
    function setGridVisible(on) {
      gridVisible = on !== false;
      if (el && el.classList) el.classList.toggle("fresco--no-grid", !gridVisible);
      gridKey = "";
      syncGrid();
      bus._emit("grid", { visible: gridVisible });
    }

    function getGridVisible() { return gridVisible; }

    // Pins the dot grid to the canvas: the same dots throughout, carried by
    // a pan and spread out or drawn in by a zoom. Their spacing is fixed in
    // canvas units the first time the grid is painted — GRID_PITCH px apart
    // at the opening view — and never changes after, so nothing comes or
    // goes. Dots grow as you zoom in and shrink as you zoom out (about a
    // pixel at home), more gently than the spacing so they never blot. Only
    // where the dots would pack into a smear (a board zoomed far out) does
    // the whole grid fade, between GRID_FADE_FROM and GRID_FADE_TO px. A dot
    // sits at each tile's centre, hence the half-pitch shift; 90° turns map
    // the lattice onto itself, so rotation needs nothing.
    var GRID_PITCH = 24;
    var GRID_FADE_FROM = 8, GRID_FADE_TO = 4;
    var gridCanvasPitch = null, gridHome = null;
    var gridKey = "";
    function syncGrid() {
      if (!gridVisible || !(s > 0) || !el || !el.style) return;
      if (gridCanvasPitch === null) {
        // Not before the picture is in: home is only known then.
        if (!ready || !(sHome > 0)) return;
        gridHome = sHome;
        gridCanvasPitch = GRID_PITCH / sHome;
      }
      var pitch = gridCanvasPitch * s;
      var alpha = clamp((pitch - GRID_FADE_TO) / (GRID_FADE_FROM - GRID_FADE_TO), 0, 1);
      var r = clamp(Math.sqrt(s / gridHome), 0.5, 3);
      var offX = mod(tx - pitch / 2, pitch), offY = mod(ty - pitch / 2, pitch);
      var key = [pitch, alpha, r, offX, offY].map(function(v) { return v.toFixed(2); }).join(",");
      if (key === gridKey) return;
      gridKey = key;
      var st = el.style;
      st.setProperty("--fresco-grid-pitch", pitch.toFixed(3) + "px");
      st.setProperty("--fresco-grid-alpha", Math.round(alpha * 100) + "%");
      st.setProperty("--fresco-grid-r", r.toFixed(2) + "px");
      st.setProperty("--fresco-grid-x", offX.toFixed(2) + "px");
      st.setProperty("--fresco-grid-y", offY.toFixed(2) + "px");
      if (!el.hasAttribute("data-fresco-grid")) el.setAttribute("data-fresco-grid", "");
    }

    // The host can rewrite the viewer's own attributes — a LiveView patch
    // resets `style` and `class` to what its template says. Whatever the
    // grid had put there is put back straight away instead of waiting for
    // the next pan.
    var gridGuard = null;
    if (typeof MutationObserver === "function" && el) {
      gridGuard = new MutationObserver(function() {
        // Only ever put back — a host that writes the class itself keeps it.
        if (!gridVisible && !el.classList.contains("fresco--no-grid")) {
          el.classList.add("fresco--no-grid");
        }
        if (gridVisible && gridCanvasPitch !== null &&
            !el.style.getPropertyValue("--fresco-grid-pitch")) {
          gridKey = "";
          syncGrid();
        }
      });
      gridGuard.observe(el, { attributes: true, attributeFilter: ["style", "class"] });
    }

    function mod(a, n) { return ((a % n) + n) % n; }

    // ↑ ↑ ↓ ↓ ← → ← → B A: a ring of dots ripples outward from the pointer
    // (or the middle) across the picture, like a stone dropped in a pond,
    // and settles. Purely a look:
    // nothing is saved, nothing else moves, and it never fires from a key
    // typed into a field. Once ↑ ↑ ↓ ↓ is in, the arrows that follow are
    // the code's, not the host's — a gallery's ← / → would otherwise flip
    // to the next picture half way through.
    var KONAMI = ["ArrowUp", "ArrowUp", "ArrowDown", "ArrowDown",
      "ArrowLeft", "ArrowRight", "ArrowLeft", "ArrowRight", "b", "a"];
    var KONAMI_IDLE_MS = 2500;
    var konamiAt = 0, konamiLast = 0;
    var konamiPointer = null;
    var rippleFrame = null;

    function onKonamiPointer(e) {
      var r = el.getBoundingClientRect();
      konamiPointer = { x: e.clientX - r.left, y: e.clientY - r.top };
    }
    function onKonamiPointerLeave() { konamiPointer = null; }

    function onKonamiKey(e) {
      if (!el.isConnected) return;
      var t = e.target;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName || ""))) return;
      var r = el.getBoundingClientRect();
      if (!(r.width > 0 && r.height > 0)) return;
      var now = Date.now();
      if (now - konamiLast > KONAMI_IDLE_MS) konamiAt = 0;
      konamiLast = now;
      var key = e.key && e.key.length === 1 ? e.key.toLowerCase() : e.key;
      if (key === KONAMI[konamiAt]) konamiAt++;
      else konamiAt = key === KONAMI[0] ? 1 : 0;
      // Past ↑ ↑ ↓ ↓, the arrows and letters are spoken for.
      if (konamiAt > 4) { e.preventDefault(); e.stopImmediatePropagation(); }
      if (konamiAt === KONAMI.length) {
        konamiAt = 0;
        ripple();
      }
    }

    function ripple() {
      var reduce = false;
      try { reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch (_) {}
      if (reduce || rippleFrame || !(s > 0)) return;
      var rect = el.getBoundingClientRect();
      var w = rect.width, h = rect.height;
      var dpr = window.devicePixelRatio || 1;
      var cv = document.createElement("canvas");
      cv.className = "fresco-ripple";
      cv.width = Math.round(w * dpr); cv.height = Math.round(h * dpr);
      cv.style.cssText = "position:absolute;left:0;top:0;width:" + w + "px;height:" + h + "px;pointer-events:none;";
      // Just above the picture: a picture usually fills the view, and a
      // ripple behind it would only show in the margins. Under whatever a
      // host lays over the stage (drawings, controls) all the same.
      if (stage && stage.parentNode === el) el.insertBefore(cv, stage.nextSibling);
      else el.appendChild(cv);
      var ctx = cv.getContext("2d");
      if (!ctx) { cv.parentNode.removeChild(cv); return; }
      var ox = konamiPointer ? konamiPointer.x : w / 2;
      var oy = konamiPointer ? konamiPointer.y : h / 2;
      var reach = Math.max(Math.hypot(ox, oy), Math.hypot(w - ox, oy),
        Math.hypot(ox, h - oy), Math.hypot(w - ox, h - oy));
      var DURATION = 1800;
      var start = null;
      function frame(ts) {
        if (start === null) start = ts;
        var t = (ts - start) / DURATION;
        if (t >= 1 || !el.isConnected) {
          rippleFrame = null;
          if (cv.parentNode) cv.parentNode.removeChild(cv);
          return;
        }
        // The board's own lattice this frame, so a pan mid-ripple carries
        // the ring with it.
        var pitch = (gridCanvasPitch || GRID_PITCH / (sHome || s)) * s;
        if (pitch < 8) pitch *= Math.ceil(8 / pitch);
        var baseR = clamp(Math.sqrt(s / (gridHome || sHome || s)), 0.75, 3);
        var offX = mod(tx - pitch / 2, pitch), offY = mod(ty - pitch / 2, pitch);
        // Only the crest is drawn: a ring a couple of dots wide running out
        // past the far corner, lifting the dots it passes outward, and
        // fading as it spreads.
        var ease = 1 - Math.pow(1 - t, 2);
        var front = ease * (reach + pitch * 3);
        var width = pitch * 1.4;
        var fade = 1 - t;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, w, h);
        ctx.fillStyle = "#ffffff";
        ctx.strokeStyle = "rgba(0, 0, 0, 0.35)";
        ctx.lineWidth = 1;
        var x0 = Math.max(offX - pitch, ox - front - width * 3);
        var x1 = Math.min(w + pitch, ox + front + width * 3);
        for (var y = offY - pitch; y < h + pitch; y += pitch) {
          for (var x = offX - pitch + Math.floor((x0 - offX + pitch) / pitch) * pitch; x < x1; x += pitch) {
            var dx = x - ox, dy = y - oy;
            var d = Math.sqrt(dx * dx + dy * dy) || 1;
            var g = Math.exp(-Math.pow((d - front) / width, 2));
            if (g < 0.04) continue;
            var k = pitch * 0.6 * g * fade / d;
            ctx.globalAlpha = g * (0.35 + 0.65 * fade);
            ctx.beginPath();
            ctx.arc(x + dx * k, y + dy * k, baseR * (1 + 1.6 * g), 0, Math.PI * 2);
            ctx.fill();
            ctx.stroke();
          }
        }
        rippleFrame = requestAnimationFrame(frame);
      }
      rippleFrame = requestAnimationFrame(frame);
    }

    function setRotation(deg) {
      var next = normalizeRotation(deg);
      if (next === rot) return;
      var previous = rot;
      rot = next;
      requestHome();
      bus._emit("rotate", { rotation: rot, previous: previous });
    }

    function getRotation() { return rot; }

    // Convenience for "rotate by N degrees" toggle buttons. The
    // delta is added to the current rotation, then snapped + normalized
    // by `setRotation`.
    function rotateBy(delta) { setRotation(rot + (delta || 0)); }

    function requestHome() {
      if (customHome) {
        try { customHome(); } catch (e) {
          if (typeof console !== "undefined" && console.error) {
            console.error("[Fresco] customHome threw:", e);
          }
        }
      } else {
        fit();
      }
    }

    // Full "reset view": zoom, pan AND rotation. The bare `requestHome()`
    // only re-fits zoom/pan (it's the internal re-home that `setRotation`
    // itself calls, so it must NOT touch rotation). The Reset-view nav
    // button and the "0" key route here instead, so a user who rotated with
    // the +90° button gets rotation cleared back to `homeRotation` too.
    // Rotation is reset inline (not via `setRotation`) to avoid its extra
    // re-home; a single `requestHome()` then fits against the reset rotation.
    function resetView() {
      if (rot !== homeRotation) {
        var previous = rot;
        rot = homeRotation;
        bus._emit("rotate", { rotation: rot, previous: previous });
      }
      requestHome();
    }

    // Allowlists. Passing an array of strings limits which gestures
    // and nav buttons are enabled. null = all enabled (default).
    function setEnabledGestures(arr) {
      enabledGestures = Array.isArray(arr) ? new Set(arr) : null;
    }
    function setEnabledNavButtons(arr) {
      enabledNavButtons = Array.isArray(arr) ? new Set(arr) : null;
    }
    function gestureEnabled(name) {
      return enabledGestures == null || enabledGestures.has(name);
    }
    function navButtonEnabled(name) {
      return enabledNavButtons == null || enabledNavButtons.has(name);
    }

    return {
      el: el,
      stage: stage,
      navEl: navEl,
      bus: bus,
      fit: fit,
      zoomAt: zoomAt,
      panBy: panBy,
      setTransform: setTransform,
      refresh: refresh,
      requestFrame: requestFrame,
      getTransform: function() {
        return { tx: tx, ty: ty, s: s, rotation: rot };
      },
      getViewportSize: function() { return { vw: vw, vh: vh }; },
      getNaturalSize: function() { return { w: nw, h: nh }; },
      isInfiniteCanvas: function() { return infiniteCanvas; },
      isReady: function() { return ready; },
      setReady: function(b) { ready = b; },
      setZoomFloor: setZoomFloor,
      // The consumer-set floor (null = engine default). Peers that lower
      // the floor temporarily (Etcher, to let a board zoom out to ink
      // outside the picture) read it first so they can restore the
      // HOST'S floor, not blindly null it.
      getZoomFloor: function() { return customSMin; },
      // "bounds" when the host asked for widened pan bounds to be centred
      // (`pan_centre: :bounds`), "content" otherwise. A peer that widens
      // them (Etcher) shapes them to suit.
      getPanCentre: function() { return panCentreBounds ? "bounds" : "content"; },
      setZoomCeiling: setZoomCeiling,
      setPanLocked: setPanLocked,
      setTwoFingerPanInverted: setTwoFingerPanInverted,
      getTwoFingerPanInverted: getTwoFingerPanInverted,
      setPanBounds: setPanBounds,
      setHomeAction: setHomeAction,
      suppressNextTap: suppressNextTap,
      setGridVisible: setGridVisible,
      getGridVisible: getGridVisible,
      setRotation: setRotation,
      getRotation: getRotation,
      rotateBy: rotateBy,
      // Named quarter-turn helpers matching the two rotate nav buttons:
      // right = clockwise (+90), left = counter-clockwise (-90). Both fire
      // the `rotate` event (persisted when `persist_rotation` is on), so a
      // host's own buttons rotate and save exactly like the built-in ones.
      rotateRight: function() { rotateBy(90); },
      rotateLeft: function() { rotateBy(-90); },
      // Programmatic equivalents of the built-in nav buttons so a
      // consumer hiding the chrome can still wire their own buttons,
      // keyboard shortcuts, or accessibility affordances to the same
      // behavior. Identical step factors + anchors as the built-in
      // buttons (1.4× / 1/1.4× zoom around viewport center;
      // requestHome flows through any active `customHome`).
      zoomIn: zoomIn,
      zoomOut: zoomOut,
      // The zoom in real pixels: 100 = one natural pixel per physical
      // screen pixel. Meaningful whether or not `actual_size` is on.
      getZoomPercent: zoomPercent,
      // At the opening view (fit, or 100% for a small picture) — where a
      // drag has nothing to pan, so a host may read it as "next picture".
      // The same allowance the built-in swipe uses.
      isAtHome: function() { return s <= sHome * DBL_ZOOMED; },
      zoomToActualSize: zoomToActualSize,
      toggleActualSize: toggleActualSize,
      fitToView: fitToView,
      toggleFullscreen: toggleFullscreen,
      requestHome: requestHome,
      setEnabledGestures: setEnabledGestures,
      setEnabledNavButtons: setEnabledNavButtons,
      gestureEnabled: gestureEnabled,
      navButtonEnabled: navButtonEnabled,
      animateTo: animateTo,
      cancelAnimation: cancelAnimation,
      teardown: teardown
    };
  }

  // ===========================================================================
  // <Fresco.viewer> mount — single image, the simple case. Wraps the engine
  // with image-load handling and source-swap methods.
  // ===========================================================================

  function mountFrescoViewer(el) {
    var stage = el.querySelector("[data-fresco-stage]") || el.querySelector(".fresco-stage");
    var img = stage && stage.querySelector("[data-fresco-img]");
    if (!stage || !img) {
      console.warn("[Fresco] mount: missing .fresco-stage or <img> inside", el);
      return null;
    }

    // CSS resets like Tailwind preflight set `img { max-width: 100% }` which
    // shrinks the layout box and breaks the transform math. Override inline
    // so any later stylesheet rule can't steal it back.
    el.style.touchAction = "none";
    img.style.maxWidth = "none";
    img.style.maxHeight = "none";
    img.style.width = "auto";
    img.style.height = "auto";

    var infiniteCanvas = el.dataset.infiniteCanvas === "true";
    var currentSrc = img.getAttribute("src") || el.dataset.src || "";

    var attrOpts = readConstraintAttrs(el);
    var engine = createTransformEngine({
      el: el,
      stage: stage,
      infiniteCanvas: infiniteCanvas,
      zoomFloor: attrOpts.zoomFloor,
      zoomCeiling: attrOpts.zoomCeiling,
      panLocked: attrOpts.panLocked,
      invertTwoFingerPan: attrOpts.invertTwoFingerPan,
      actualSize: attrOpts.actualSize,
      navLayout: attrOpts.navLayout,
      navOverflow: attrOpts.navOverflow,
      navReverse: attrOpts.navReverse,
      gestures: attrOpts.gestures,
      navButtons: attrOpts.navButtons,
      rotation: attrOpts.rotation,
      getNaturalSize: function() {
        return {
          w: img.naturalWidth || img.width || 0,
          h: img.naturalHeight || img.height || 0
        };
      },
      applyChildren: function(s) {
        var iw = img.naturalWidth || img.width || 0;
        var ih = img.naturalHeight || img.height || 0;
        img.style.width = (iw * s) + "px";
        img.style.height = (ih * s) + "px";
      }
    });

    function markReady() {
      // CSS rule `.fresco-viewer:not(.fresco--ready) .fresco-stage img`
      // hides the img until this class is on the host. Without it, a
      // high-res image renders at natural size in the DOM (clipped to
      // top-left by `overflow: hidden`) during the load window — the
      // user sees a chunk of the natural image and can't pan to the
      // rest. The class flips on the first successful fit or on error.
      el.classList.add("fresco--ready");
    }

    function doFit() {
      engine.fit();
      engine.setReady(true);
      markReady();
      engine.bus._emit("open", {
        src: currentSrc,
        naturalWidth: img.naturalWidth,
        naturalHeight: img.naturalHeight
      });
      // Kick off decode() AFTER fitting. The old code waited on decode
      // before fitting, which for huge images (multi-second decode)
      // left the image visible at natural size in the meantime. We
      // need the natural dimensions, not the decoded bitmap, to fit.
      // The browser will GPU-upload the bitmap asynchronously; the
      // worst case is a brief blur on the first frame.
      if (typeof img.decode === "function") {
        try { img.decode().catch(function() {}); } catch (_) {}
      }
    }

    function initEngineFromImg() { doFit(); }
    function onImgLoad() { initEngineFromImg(); }

    function onImgError() {
      // Image failed to load. Mark the engine ready anyway so the UI
      // is responsive — the host will show whatever the browser
      // renders for a broken image (usually a placeholder icon). A
      // bus "error" event lets consumers surface a friendlier
      // message of their own.
      engine.setReady(true);
      markReady();
      engine.bus._emit("error", { src: currentSrc });
    }

    function setSource(url) {
      if (!url) return;
      currentSrc = url;
      var resolved = resolveTileSource(url);
      if (resolved.type !== "image") {
        console.error(
          "[Fresco] tile-source types other than \"image\" aren't supported in 0.5.x — " +
          "Tessera integration is planned for a later release."
        );
        return;
      }
      engine.setReady(false);
      el.classList.remove("fresco--ready");
      img.addEventListener("load", onImgLoad, { once: true });
      img.addEventListener("error", onImgError, { once: true });
      img.src = resolved.url;
    }

    function swapSourcePreservingBounds(url) {
      if (!url) return;
      currentSrc = url;
      var resolved = resolveTileSource(url);
      if (resolved.type !== "image") {
        console.error(
          "[Fresco] tile-source types other than \"image\" aren't supported in 0.5.x — " +
          "Tessera integration is planned for a later release."
        );
        return;
      }
      var t0 = engine.getTransform();
      img.addEventListener("load", function once() {
        img.removeEventListener("load", once);
        engine.refresh();
        engine.setTransform(t0.tx, t0.ty, t0.s);
        markReady();
        engine.bus._emit("open", {
          src: currentSrc,
          naturalWidth: img.naturalWidth,
          naturalHeight: img.naturalHeight
        });
      }, { once: true });
      img.src = resolved.url;
    }

    // Init as soon as natural dimensions are available (which is
    // typically the moment the header bytes are decoded, well before
    // the full image is loaded). This avoids the long "top-left
    // clipped chunk" window for high-res images. If naturalWidth
    // isn't there yet, wait on `load` / `error`.
    if (img.naturalWidth > 0) {
      initEngineFromImg();
      // Re-run on load as well, in case naturalWidth wasn't final
      // when we ran the first fit (rare but possible for streaming
      // image formats).
      if (!img.complete) {
        img.addEventListener("load", onImgLoad, { once: true });
      }
    } else {
      img.addEventListener("load", onImgLoad, { once: true });
      img.addEventListener("error", onImgError, { once: true });
    }

    // Apply opt-in zoom/pan constraint attrs (data-zoom-floor /
    // data-zoom-ceiling / data-pan-locked from the Phoenix component).
    // No-op when absent so existing consumers see identical behavior.
    applyConstraintAttrs(el, engine);

    return {
      el: el,
      stage: stage,
      img: img,
      navEl: engine.navEl,
      bus: engine.bus,
      getTransform: engine.getTransform,
      getViewportSize: engine.getViewportSize,
      getImageSize: function() {
        return { iw: img.naturalWidth || 0, ih: img.naturalHeight || 0 };
      },
      isInfiniteCanvas: engine.isInfiniteCanvas,
      getCurrentSrc: function() { return currentSrc; },
      fit: engine.fit,
      zoomAt: engine.zoomAt,
      panBy: engine.panBy,
      setTransform: engine.setTransform,
      setZoomFloor: engine.setZoomFloor,
      getZoomFloor: engine.getZoomFloor,
      getPanCentre: engine.getPanCentre,
      setZoomCeiling: engine.setZoomCeiling,
      setPanLocked: engine.setPanLocked,
      setTwoFingerPanInverted: engine.setTwoFingerPanInverted,
      getTwoFingerPanInverted: engine.getTwoFingerPanInverted,
      setRotation: engine.setRotation,
      getRotation: engine.getRotation,
      rotateBy: engine.rotateBy,
      getZoomPercent: engine.getZoomPercent,
      isAtHome: engine.isAtHome,
      zoomToActualSize: engine.zoomToActualSize,
      toggleActualSize: engine.toggleActualSize,
      fitToView: engine.fitToView,
      setSource: setSource,
      swapSourcePreservingBounds: swapSourcePreservingBounds,
      teardown: engine.teardown
    };
  }

  // ===========================================================================
  // Viewer handle — public surface via window.Fresco.viewerFor(id).
  // ===========================================================================

  function makeViewerHandle(controller) {
    var bus = controller.bus;
    var el = controller.el;

    // image-pixel → screen-pixel under composite transform
    //   screen = rect.{left,top} + translate(tx, ty) · rotate(rot) · scale(s) · image
    // For 90°-snapped rotations the cos/sin pair is exact (see
    // `rotationCosSin`) so the formula reduces to swaps + negations
    // — no trig at the hot path.
    function imageToScreen(pt) {
      var t = controller.getTransform();
      var rect = el.getBoundingClientRect();
      var cs = rotationCosSin(t.rotation || 0);
      var px = (pt.x || 0) * t.s;
      var py = (pt.y || 0) * t.s;
      return {
        x: rect.left + t.tx + px * cs.c - py * cs.sn,
        y: rect.top  + t.ty + px * cs.sn + py * cs.c
      };
    }

    // Inverse of imageToScreen — subtract translate, apply inverse
    // rotation (transpose of the rotation matrix), then divide by
    // scale to get image-pixel coords.
    function screenToImage(pt) {
      var t = controller.getTransform();
      var rect = el.getBoundingClientRect();
      var cs = rotationCosSin(t.rotation || 0);
      var dx = (pt.x || 0) - rect.left - t.tx;
      var dy = (pt.y || 0) - rect.top  - t.ty;
      return {
        x: ( dx * cs.c + dy * cs.sn) / t.s,
        y: (-dx * cs.sn + dy * cs.c) / t.s
      };
    }

    function getViewportBounds() {
      var t = controller.getTransform();
      var v = controller.getViewportSize();
      return {
        x: -t.tx / t.s,
        y: -t.ty / t.s,
        width: v.vw / t.s,
        height: v.vh / t.s
      };
    }

    function fitBounds(rect, opts) {
      if (!rect || rect.width <= 0 || rect.height <= 0) return;
      var v = controller.getViewportSize();
      var t = controller.getTransform();
      var rot = t.rotation || 0;
      // The rect is in unrotated canvas-px (the consumer's natural
      // frame of reference). After rotation, its screen-space bbox
      // has different dims — for 90° / 270° the width and height
      // swap. Compute newS against the rotated bbox so the rotated
      // content actually fits the viewport.
      var fitW = (rot === 90 || rot === 270) ? rect.height : rect.width;
      var fitH = (rot === 90 || rot === 270) ? rect.width  : rect.height;
      var newS = Math.min(v.vw / fitW, v.vh / fitH);
      // Compute the rotated + scaled bbox of `rect` in stage-local
      // coords (pre-translate). The same math as `rotatedRectBBox`
      // but inlined since the engine's helper isn't exposed.
      var cs = rotationCosSin(rot);
      var x1 = rect.x, y1 = rect.y;
      var x2 = rect.x + rect.width, y2 = rect.y + rect.height;
      var pts = [[x1, y1], [x2, y1], [x2, y2], [x1, y2]];
      var minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (var i = 0; i < 4; i++) {
        var px = pts[i][0] * newS * cs.c - pts[i][1] * newS * cs.sn;
        var py = pts[i][0] * newS * cs.sn + pts[i][1] * newS * cs.c;
        if (px < minX) minX = px;
        if (px > maxX) maxX = px;
        if (py < minY) minY = py;
        if (py > maxY) maxY = py;
      }
      // Center the rotated bbox in the viewport.
      var newTx = (v.vw - (maxX - minX)) / 2 - minX;
      var newTy = (v.vh - (maxY - minY)) / 2 - minY;
      if (opts && opts.animate) {
        controller.animateTo(newTx, newTy, newS, {
          duration: opts.duration,
          easing: opts.easing
        });
      } else {
        controller.setTransform(newTx, newTy, newS);
      }
    }

    return {
      container: el,
      imageToScreen: imageToScreen,
      screenToImage: screenToImage,
      getViewportBounds: getViewportBounds,
      fitBounds: fitBounds,
      setSource: function(url) { controller.setSource(url); },
      swapSourcePreservingBounds: function(url) { controller.swapSourcePreservingBounds(url); },
      // Zoom in real pixels (`actual_size`) — as on the canvas handle.
      getZoomPercent:   function() { return controller.getZoomPercent(); },
      isAtHome:         function() { return controller.isAtHome ? controller.isAtHome() : true; },
      zoomToActualSize: function() { controller.zoomToActualSize(); },
      toggleActualSize: function() { controller.toggleActualSize(); },
      fitToView:        function() { controller.fitToView(); },
      // Opt-in zoom + pan constraint controls (0.5.1+). All three are
      // no-ops at their defaults (null / null / false), so existing
      // consumers see identical behavior unless they call these. See
      // the engine's setZoomFloor / setZoomCeiling / setPanLocked
      // docstrings for semantics.
      setZoomFloor:   function(v) { controller.setZoomFloor(v); },
      getZoomFloor:   function() { return controller.getZoomFloor(); },
      setZoomCeiling: function(v) { controller.setZoomCeiling(v); },
      setPanLocked:   function(b) { controller.setPanLocked(b); },
      setTwoFingerPanInverted: function(b) { controller.setTwoFingerPanInverted(b); },
      getTwoFingerPanInverted: function() { return controller.getTwoFingerPanInverted(); },
      // Rotation controls — documented on <Fresco.viewer>'s
      // :initial_rotation attr since 0.5.7 but only ever exposed on the
      // canvas handle; the engine has supported them all along (the
      // built-in rotate nav button calls the same controller methods).
      setGridVisible: function(on) { controller.setGridVisible(on); },
      getGridVisible: function() { return controller.getGridVisible(); },
      setRotation:    function(deg) { controller.setRotation(deg); },
      getRotation:    function() { return controller.getRotation(); },
      rotateBy:       function(delta) { controller.rotateBy(delta); },
      // Named quarter-turn helpers (right = +90 CW, left = -90 CCW), the
      // twins of the two rotate nav buttons. Both persist like the buttons.
      rotateRight:    function() { controller.rotateBy(90); },
      rotateLeft:     function() { controller.rotateBy(-90); },
      on: bus.on,
      _emit: bus._emit,
      appendNavButton: function(svg, title, onClick, opts) {
        return attachNavButton(controller.navEl, svg, title, onClick, opts);
      }
    };
  }

  // ===========================================================================
  // FrescoViewer LiveView hook
  // ===========================================================================

  window.FrescoHooks = window.FrescoHooks || {};

  window.FrescoHooks.FrescoViewer = {
    mounted: function() {
      injectStyles();
      var controller = mountFrescoViewer(this.el);
      if (!controller) return;
      this.controller = controller;
      var handle = makeViewerHandle(controller);
      this.handle = handle;
      publishReady(this.el.id, handle);
      this._rotateOff = wireRotationBridge(this);
    },

    updated: function() {
      if (!this.controller) return;
      var next = this.el.dataset.src;
      if (next && next !== this.controller.getCurrentSrc()) {
        this.controller.swapSourcePreservingBounds(next);
      }
    },

    destroyed: function() {
      if (this._rotateOff) { try { this._rotateOff(); } catch (_) {} this._rotateOff = null; }
      if (this.el && this.el.id) unpublish(this.el.id);
      if (this.controller) {
        try { this.controller.teardown(); } catch (_) {}
        this.controller = null;
      }
      this.handle = null;
    }
  };

  // ===========================================================================
  // <Fresco.canvas> mount — N images laid out at canvas-pixel coords.
  //
  // The host carries data-canvas-width/-height. The stage holds N <img>
  // children with data-canvas-x/-y/-width (and optional data-canvas-height,
  // data-image-id, data-z-index). The engine's applyChildren(s) walks the
  // imgs every frame and rewrites each one's left/top/width/height.
  // Single-image is just N=1.
  //
  // The canvas handle adds: getCanvasSize, getImages, imageBoundsFor,
  // fitImage, getExtension (read-only; extensions write through LiveView).
  // Coordinates operate in canvas-pixel space — same coord system the
  // .fresco file uses, so annotation payloads compose uniformly.
  // ===========================================================================

  function mountFrescoCanvas(el) {
    var stage = el.querySelector("[data-fresco-stage]") || el.querySelector(".fresco-stage");
    if (!stage) {
      console.warn("[Fresco] canvas mount: missing .fresco-stage", el);
      return null;
    }

    el.style.touchAction = "none";

    var canvasW = parseFloat(el.dataset.canvasWidth) || 0;
    var canvasH = parseFloat(el.dataset.canvasHeight) || 0;
    var infiniteCanvas = el.dataset.infiniteCanvas === "true";

    var imgs = Array.from(stage.querySelectorAll("[data-fresco-canvas-img]"));
    function applyImgResets(im) {
      im.style.maxWidth = "none";
      im.style.maxHeight = "none";
      // Clear the server's pre-fit hints (Fresco.Canvas.image_style/2): the
      // markup paints a CSS-contained preview for the gap before this engine
      // exists, and `object-fit` is the one declaration the per-frame math
      // below does not overwrite.
      im.style.objectFit = "";
      if (im.hasAttribute("data-fresco-prefit")) im.style.transform = ""; // actual_size centring
      im.removeAttribute("data-fresco-prefit");
    }
    imgs.forEach(applyImgResets);
    // Same for the stage: the preview sizes it to the viewer so the images'
    // percentages have something to resolve against. The engine only ever
    // transforms the stage, so its box goes back to shrink-wrapping content.
    stage.style.width = "";
    stage.style.height = "";

    // Set of image ids the consumer has explicitly hidden via
    // setImageVisible(id, false). Hidden imgs are still in `imgs` and
    // still participate in layout / pan-bounds math; applyChildren just
    // sets visibility:hidden on them and skips position writes (cheap
    // optimization).
    var hiddenImageIds = new Set();

    // Memory windowing — 0/null disables. When > 0, the engine evicts
    // src for images whose canvas-pixel rect is more than `memoryWindow`
    // viewport-widths/heights away from the current viewport. Frame
    // counter throttles the recomputation to once every 8 animation
    // frames so pan doesn't tank.
    var memoryWindow = 0;
    var memoryFrameCounter = 0;

    function imgRect(im) {
      var x = parseFloat(im.dataset.canvasX) || 0;
      var y = parseFloat(im.dataset.canvasY) || 0;
      var w = parseFloat(im.dataset.canvasWidth) || 0;
      var dh = parseFloat(im.dataset.canvasHeight);
      var h;
      if (dh > 0) {
        h = dh;
      } else if (im.naturalWidth > 0 && im.naturalHeight > 0 && w > 0) {
        h = w * (im.naturalHeight / im.naturalWidth);
      } else {
        h = 0;
      }
      return { x: x, y: y, width: w, height: h };
    }

    var canvasAttrOpts = readConstraintAttrs(el);
    var engine = createTransformEngine({
      el: el,
      stage: stage,
      infiniteCanvas: infiniteCanvas,
      zoomFloor: canvasAttrOpts.zoomFloor,
      zoomCeiling: canvasAttrOpts.zoomCeiling,
      panLocked: canvasAttrOpts.panLocked,
      invertTwoFingerPan: canvasAttrOpts.invertTwoFingerPan,
      panCentreBounds: canvasAttrOpts.panCentreBounds,
      actualSize: canvasAttrOpts.actualSize,
      navLayout: canvasAttrOpts.navLayout,
      navOverflow: canvasAttrOpts.navOverflow,
      navReverse: canvasAttrOpts.navReverse,
      gestures: canvasAttrOpts.gestures,
      navButtons: canvasAttrOpts.navButtons,
      rotation: canvasAttrOpts.rotation,
      getNaturalSize: function() { return { w: canvasW, h: canvasH }; },
      applyChildren: function(s) {
        for (var i = 0; i < imgs.length; i++) {
          var im = imgs[i];
          var hidden = hiddenImageIds.has(im.dataset.imageId);
          if (hidden) {
            im.style.visibility = "hidden";
            continue;
          }
          if (im.style.visibility === "hidden") im.style.visibility = "";
          var r = imgRect(im);
          im.style.left = (r.x * s) + "px";
          im.style.top = (r.y * s) + "px";
          im.style.width = (r.width * s) + "px";
          if (r.height > 0) {
            im.style.height = (r.height * s) + "px";
          }
        }
      }
    });

    // Hydrate from server-rendered HTML and run initial fit. Canvas dims are
    // known immediately — no need to wait for image loads. Per-image natural
    // dims arrive later via load events; we requestFrame on each load so
    // heights derived from natural aspect ratio settle in cleanly.
    //
    // 0.6.0 — `:initial_fit_image_id` / `:initial_fit_bounds` data-attrs
    // override the canvas-wide fit at first paint. image-id wins if both
    // present. If image-id doesn't match any image, console.warn and
    // fall back to the canvas-wide fit.
    function initialFit() {
      var initialImageId = el.dataset.initialFitImageId || null;
      var initialBoundsJson = el.dataset.initialFitBounds || null;
      var initialBounds = null;
      if (initialBoundsJson) {
        try {
          initialBounds = JSON.parse(initialBoundsJson);
        } catch (_) {
          console.warn("[Fresco] data-initial-fit-bounds is not valid JSON; falling back to canvas fit");
        }
      }
      var fitDone = false;
      if (initialImageId) {
        var b = imageBoundsFor(initialImageId);
        if (b) {
          // Manual fitBounds (we can't call the handle method yet — handle
          // is constructed after this function).
          var v = engine.getViewportSize();
          var newS = Math.min(v.vw / b.width, v.vh / b.height);
          var newTx = (v.vw - newS * b.width) / 2 - newS * b.x;
          var newTy = (v.vh - newS * b.height) / 2 - newS * b.y;
          engine.setTransform(newTx, newTy, newS);
          fitDone = true;
        } else {
          console.warn(
            "[Fresco] data-initial-fit-image-id=\"" + initialImageId +
            "\" doesn't match any image; falling back to canvas fit"
          );
        }
      }
      if (!fitDone && initialBounds && typeof initialBounds.x === "number" &&
          typeof initialBounds.y === "number" &&
          typeof initialBounds.width === "number" &&
          typeof initialBounds.height === "number" &&
          initialBounds.width > 0 && initialBounds.height > 0) {
        var v2 = engine.getViewportSize();
        var newS2 = Math.min(v2.vw / initialBounds.width, v2.vh / initialBounds.height);
        var newTx2 = (v2.vw - newS2 * initialBounds.width) / 2 - newS2 * initialBounds.x;
        var newTy2 = (v2.vh - newS2 * initialBounds.height) / 2 - newS2 * initialBounds.y;
        engine.setTransform(newTx2, newTy2, newS2);
        fitDone = true;
      }
      if (!fitDone) engine.fit();
      engine.setReady(true);
      // CSS rule `.fresco-viewer:not(.fresco--ready) .fresco-stage img`
      // hides the imgs until first fit. Canvas knows dims at mount so
      // we apply the class right after initialFit — no flash window.
      el.classList.add("fresco--ready");
      engine.bus._emit("open", {
        canvasWidth: canvasW,
        canvasHeight: canvasH,
        imageCount: imgs.length
      });
    }
    initialFit();

    // 0.5.x leftover — runtime overrides applied after the initial fit so
    // the floor / lock take effect immediately. Currently a no-op (the
    // engine reads everything from opts at construction now); kept as a
    // safe extension point for future post-construct attrs.
    applyConstraintAttrs(el, engine);

    // 0.6.0 — auto-evict src for images far from the viewport. The attr
    // reading is here (not in readConstraintAttrs) because memory
    // windowing is canvas-only and depends on the canvas handle's
    // imgRect helper.
    var mw = parseInt(el.dataset.memoryWindow || "0", 10);
    if (!isNaN(mw) && mw > 0) {
      memoryWindow = mw;
      recomputeMemoryWindow();
    }

    function onImgLoad(e) {
      var im = e.target;
      engine.bus._emit("image-loaded", {
        imageId: im.dataset.imageId,
        naturalWidth: im.naturalWidth,
        naturalHeight: im.naturalHeight
      });
      engine.requestFrame();
    }
    imgs.forEach(function(im) {
      if (!im.complete) im.addEventListener("load", onImgLoad);
      else if (im.naturalWidth > 0) {
        engine.bus._emit("image-loaded", {
          imageId: im.dataset.imageId,
          naturalWidth: im.naturalWidth,
          naturalHeight: im.naturalHeight
        });
      }
    });

    // Re-read canvas dims and images list — called from the hook's `updated`
    // callback when the server-rendered layout changes.
    function refreshLayout() {
      canvasW = parseFloat(el.dataset.canvasWidth) || 0;
      canvasH = parseFloat(el.dataset.canvasHeight) || 0;
      imgs = Array.from(stage.querySelectorAll("[data-fresco-canvas-img]"));
      imgs.forEach(applyImgResets);
      imgs.forEach(function(im) {
        if (!im.complete) im.addEventListener("load", onImgLoad);
      });
      engine.refresh();
      engine.requestFrame();
    }

    function imageBoundsFor(id) {
      for (var i = 0; i < imgs.length; i++) {
        if (imgs[i].dataset.imageId === id) return imgRect(imgs[i]);
      }
      return null;
    }

    function getImages() {
      return imgs.map(function(im) {
        var r = imgRect(im);
        return {
          id: im.dataset.imageId || null,
          x: r.x, y: r.y, width: r.width, height: r.height,
          z_index: parseInt(im.dataset.zIndex || im.style.zIndex || "0", 10),
          naturalWidth: im.naturalWidth || 0,
          naturalHeight: im.naturalHeight || 0,
          src: im.getAttribute("src") || ""
        };
      });
    }

    function getExtension(name) {
      var raw = el.dataset.extensions;
      if (!raw) return undefined;
      try {
        var parsed = JSON.parse(raw);
        return parsed && parsed[name];
      } catch (_) { return undefined; }
    }

    // Toggle individual image visibility without removing it from the
    // layout (so pan-bounds, annotations, fit math stay anchored).
    // Emits `image-visibility-change` on the bus so extensions
    // (Etcher, ML overlays, comment threads) that pin DOM siblings
    // to a specific image can hide them in lockstep.
    function setImageVisible(id, visible) {
      var was = hiddenImageIds.has(id);
      if (visible) hiddenImageIds.delete(id);
      else hiddenImageIds.add(id);
      var now = hiddenImageIds.has(id);
      // Flip the matching <img>'s inline visibility synchronously so a
      // consumer reading getBoundingClientRect / imageBoundsFor on the very
      // next line sees the new state — `applyChildren` (rAF-deferred) would
      // otherwise lag a frame. If the element isn't present yet (race with a
      // pending refreshLayout), skip; the next applyChildren will apply it.
      for (var i = 0; i < imgs.length; i++) {
        if (imgs[i].dataset.imageId === id) {
          imgs[i].style.visibility = visible ? "" : "hidden";
          break;
        }
      }
      if (was !== now) {
        engine.bus._emit("image-visibility-change", {
          imageId: id,
          visible: visible
        });
      }
      engine.requestFrame();
    }

    // Snapshot of the currently-hidden image ids. Used by extensions
    // mounting after the host has already called `setImageVisible(...,
    // false)` — the `image-visibility-change` event is fire-and-forget,
    // so a late mounter needs a pull-API to seed its initial state.
    function getHiddenImageIds() {
      return Array.from(hiddenImageIds);
    }

    // Memory windowing. recomputeWindow() inflates the current viewport
    // rect (in canvas coords) by `memoryWindow` viewport sizes, then
    // evicts images outside that rect and restores those inside.
    function rectsIntersect(a, b) {
      return !(a.x + a.width <= b.x || b.x + b.width <= a.x ||
               a.y + a.height <= b.y || b.y + b.height <= a.y);
    }

    function recomputeMemoryWindow() {
      if (!memoryWindow || memoryWindow <= 0) return;
      var t = engine.getTransform();
      var v = engine.getViewportSize();
      if (t.s <= 0) return;
      // Viewport rect in canvas-pixel coords (same as getViewportBounds).
      var vp = {
        x: -t.tx / t.s,
        y: -t.ty / t.s,
        width: v.vw / t.s,
        height: v.vh / t.s
      };
      // Inflate by N viewport sizes.
      var padX = vp.width * memoryWindow;
      var padY = vp.height * memoryWindow;
      var window = {
        x: vp.x - padX,
        y: vp.y - padY,
        width: vp.width + 2 * padX,
        height: vp.height + 2 * padY
      };
      for (var i = 0; i < imgs.length; i++) {
        var im = imgs[i];
        var r = imgRect(im);
        var inside = rectsIntersect(window, r);
        if (inside) {
          // Restore if previously evicted.
          if (!im.getAttribute("src") && im.dataset.frescoSrc) {
            im.setAttribute("src", im.dataset.frescoSrc);
            engine.bus._emit("image-restored", { imageId: im.dataset.imageId });
          }
        } else {
          // Evict. Stash src into data-fresco-src.
          var src = im.getAttribute("src");
          if (src) {
            im.dataset.frescoSrc = src;
            im.removeAttribute("src");
            engine.bus._emit("image-evicted", { imageId: im.dataset.imageId });
          }
        }
      }
    }

    function setMemoryWindow(n) {
      memoryWindow = (typeof n === "number" && n > 0) ? n : 0;
      memoryFrameCounter = 0;
      recomputeMemoryWindow();
    }

    // Throttle: recompute every 8 animation frames (not every frame).
    // Also recompute on resize. Hooked here so it's automatic once
    // setMemoryWindow has been called.
    engine.bus.on("animation", function() {
      if (!memoryWindow || memoryWindow <= 0) return;
      if ((++memoryFrameCounter & 7) === 0) recomputeMemoryWindow();
    });
    engine.bus.on("resize", function() {
      if (memoryWindow && memoryWindow > 0) recomputeMemoryWindow();
    });

    // ── View tracker — dominant image + focus/blur events ───────────────
    // Computes the "focused" image as the one with the highest overlap
    // ratio between its canvas-pixel rect and the current viewport rect.
    // Honors `hiddenImageIds` (hidden imgs are never dominant). The
    // tracker handles settle-time gating, page-visibility pause, and
    // event emission — see createViewTracker for the state machine.
    function computeViewportRectInCanvas() {
      var t = engine.getTransform();
      var v = engine.getViewportSize();
      if (t.s <= 0) return null;
      return {
        x: -t.tx / t.s,
        y: -t.ty / t.s,
        width: v.vw / t.s,
        height: v.vh / t.s
      };
    }

    function computeDominantCanvasImage(threshold) {
      var vp = computeViewportRectInCanvas();
      if (!vp) return null;
      var bestId = null;
      var bestRatio = 0;
      for (var i = 0; i < imgs.length; i++) {
        var im = imgs[i];
        var id = im.dataset.imageId;
        if (!id) continue;
        if (hiddenImageIds.has(id)) continue;
        var r = imgRect(im);
        if (r.width <= 0 || r.height <= 0) continue;
        var ox  = Math.max(vp.x, r.x);
        var oy  = Math.max(vp.y, r.y);
        var ox2 = Math.min(vp.x + vp.width,  r.x + r.width);
        var oy2 = Math.min(vp.y + vp.height, r.y + r.height);
        if (ox2 <= ox || oy2 <= oy) continue;
        var ratio = ((ox2 - ox) * (oy2 - oy)) / (r.width * r.height);
        if (ratio < threshold) continue;
        if (ratio > bestRatio) {
          bestRatio = ratio;
          bestId = id;
        }
      }
      return bestId;
    }

    var viewTracker = createViewTracker({
      bus: engine.bus,
      getDominantImageId: computeDominantCanvasImage
    });

    // Drive the tracker off the engine's per-frame animation event.
    // tick() is cheap (one rect intersection per image); the settleMs
    // gate inside the tracker handles pan-throughs.
    engine.bus.on("animation", function() {
      if (viewTracker.isEnabled()) viewTracker.tick();
    });

    // Honor declarative `data-view-tracking` / `-settle-ms` / `-threshold`
    // attrs at mount. Default off.
    if (el.dataset.viewTracking === "true") {
      var trackOpts = {};
      var sm = parseInt(el.dataset.viewSettleMs || "", 10);
      if (!isNaN(sm) && sm >= 0) trackOpts.settleMs = sm;
      var th = parseFloat(el.dataset.viewThreshold || "");
      if (!isNaN(th) && th > 0 && th <= 1) trackOpts.threshold = th;
      viewTracker.enable(trackOpts);
    }

    // Replace the entire image set in place, without remounting the DOM, so
    // consumer state tied to the page session (Pointer Lock, audio/video
    // pipelines, peer overlays bound via `handle.on(...)`) survives. Additive
    // + programmatic — no initial-mount path changes, fires no "user did
    // this" event. See the public-handle proxy for the documented signature.
    //
    //   sources : non-empty array of `{ src|url, x?, y?, width?|w?, height?|h?,
    //             id?, z_index?|zIndex? }` (the `getImages()` shape).
    //   opts    : { reset_view?: bool=true, extensions?: map,
    //               canvasWidth?: number, canvasHeight?: number }
    //   → Promise<void> that resolves once the first new frame is decodable;
    //     rejects synchronously on empty / malformed input.
    var pendingSourcesResolve = null;
    function setSources(sources, opts) {
      opts = opts || {};
      return new Promise(function(resolve, reject) {
        function num(v, d) { return (typeof v === "number" && isFinite(v)) ? v : d; }

        // Validate up front so a bad call can't blank the viewer mid-read,
        // and (importantly) doesn't cancel an in-flight good swap.
        if (!Array.isArray(sources) || sources.length === 0) {
          reject(new Error("[Fresco] setSources: `sources` must be a non-empty array"));
          return;
        }
        var normalized = [];
        for (var i = 0; i < sources.length; i++) {
          var s = sources[i] || {};
          var src = s.src || s.url;
          if (typeof src !== "string" || !src) {
            reject(new Error("[Fresco] setSources: image " + i + " has no `src`"));
            return;
          }
          normalized.push({
            id: (s.id != null ? String(s.id) : ("img-" + (i + 1))),
            src: src,
            x: num(s.x, 0),
            y: num(s.y, 0),
            width: num(s.width != null ? s.width : s.w, 0),
            height: num(s.height != null ? s.height : s.h, 0),
            zIndex: num(s.z_index != null ? s.z_index : s.zIndex, 0)
          });
        }

        // The id namespace just changed — drop the previous set's hidden-
        // image bookkeeping so brand-new images don't inherit a stale
        // `setImageVisible(id, false)` from the old set. Symmetric with how
        // the palette isn't preserved; a consumer that wants to carry hidden
        // state snapshots `getHiddenImageIds()` before the swap and re-applies.
        hiddenImageIds.clear();

        // A newer call supersedes an in-flight one: resolve the previous
        // Promise (consumers can't distinguish racing calls, so resolving is
        // friendlier than rejecting) and proceed with this set.
        if (pendingSourcesResolve) {
          var prev = pendingSourcesResolve;
          pendingSourcesResolve = null;
          prev();
        }

        // Cancel any in-flight gesture so a pan/zoom doesn't continue against
        // images that no longer exist. Best-effort — guarded for engines
        // without the hook; the re-fit below overrides a stale transform.
        if (typeof engine.cancelGesture === "function") {
          try { engine.cancelGesture(); } catch (_) {}
        }

        // Replace canvas-level extensions atomically with the swap (before
        // relayout) so a peer re-reading the DOM never sees new images with
        // the old extension map. Omitted → keep the current map untouched.
        if (opts.extensions && typeof opts.extensions === "object") {
          try { el.dataset.extensions = JSON.stringify(opts.extensions); } catch (_) {}
        }

        // Release the previous set's decoded bitmaps + DOM nodes.
        var old = Array.prototype.slice.call(
          stage.querySelectorAll("[data-fresco-canvas-img]")
        );
        old.forEach(function(im) {
          im.removeEventListener("load", onImgLoad);
          try { im.removeAttribute("src"); } catch (_) {}
          if (im.parentNode) im.parentNode.removeChild(im);
        });

        // Canvas extent: explicit opts → bounding box of the new images →
        // keep the current dims as a last resort.
        var cw = num(opts.canvasWidth != null ? opts.canvasWidth : opts.canvas_width, 0);
        var ch = num(opts.canvasHeight != null ? opts.canvasHeight : opts.canvas_height, 0);
        if (cw <= 0 || ch <= 0) {
          var bx = 0, by = 0;
          for (var j = 0; j < normalized.length; j++) {
            var n = normalized[j];
            if (n.x + n.width > bx) bx = n.x + n.width;
            if (n.y + n.height > by) by = n.y + n.height;
          }
          if (cw <= 0) cw = bx > 0 ? bx : canvasW;
          if (ch <= 0) ch = by > 0 ? by : canvasH;
        }
        el.dataset.canvasWidth = String(cw);
        el.dataset.canvasHeight = String(ch);

        // Build the new <img> elements, mirroring the server-rendered shape
        // (`refreshLayout` re-reads them + drives positioning from the data
        // attrs; `applyChildren` sets left/top/width/height each frame).
        normalized.forEach(function(n) {
          var im = document.createElement("img");
          im.setAttribute("data-fresco-canvas-img", "");
          im.setAttribute("data-image-id", n.id);
          im.dataset.canvasX = String(n.x);
          im.dataset.canvasY = String(n.y);
          im.dataset.canvasWidth = String(n.width);
          if (n.height > 0) im.dataset.canvasHeight = String(n.height);
          im.dataset.zIndex = String(n.zIndex);
          im.draggable = false;
          im.alt = "";
          im.style.position = "absolute";
          im.style.maxWidth = "none";
          im.style.maxHeight = "none";
          if (n.zIndex) im.style.zIndex = String(n.zIndex);
          im.src = n.src;
          stage.appendChild(im);
        });

        // Re-read imgs + dims, recompute layout (same path as the hook's
        // `updated`), then fit unless the consumer is preserving the view.
        refreshLayout();
        if (opts.reset_view !== false) engine.fit();

        // Re-emit the mount-ready signal (peers bound to `open` rebuild for
        // free) plus a dedicated `sources-changed` for swap-aware peers.
        engine.bus._emit("open", {
          canvasWidth: cw, canvasHeight: ch, imageCount: normalized.length
        });
        engine.bus._emit("sources-changed", {
          imageCount: normalized.length,
          extensionsReplaced: !!(opts.extensions && typeof opts.extensions === "object"),
          resetView: opts.reset_view !== false
        });

        // Resolve after the first post-swap frame paints — i.e. after
        // `applyChildren` has positioned/sized the new <img>s — so the
        // consumer can `imageBoundsFor(...)` / `getBoundingClientRect()` on
        // the next line and get measurable rects (not the zeros/stale rects
        // visible if we resolved at decode time). `update-viewport` fires at
        // the end of the engine's frame, just after `applyChildren`.
        var settled = false;
        var unsub = null;
        var safety = null;
        function done() {
          if (settled) return;
          settled = true;
          if (unsub) { try { unsub(); } catch (_) {} }
          if (safety) clearTimeout(safety);
          if (pendingSourcesResolve === done) pendingSourcesResolve = null;
          resolve();
        }
        pendingSourcesResolve = done;
        engine.requestFrame();                       // ensure a frame is scheduled
        unsub = engine.bus.on("update-viewport", done);
        safety = setTimeout(done, 1000);             // never hang
      });
    }

    // Swap a single image's source in place — e.g. a paged reader cycling
    // its load window between a real (proxied) URL and a lightweight
    // placeholder — without re-running the full layout. Re-latches fresco's
    // load tracking so `image-loaded` fires on the new src and the rect
    // settles once the new natural dims arrive. Returns true if an image
    // with `id` exists. Symmetric with `setImageVisible`.
    function setImageSrc(id, url) {
      if (typeof url !== "string" || !url) return false;
      for (var i = 0; i < imgs.length; i++) {
        var im = imgs[i];
        if (im.dataset.imageId === id) {
          im.src = url;
          if (!im.complete) im.addEventListener("load", onImgLoad);
          engine.requestFrame();
          return true;
        }
      }
      return false;
    }

    // Resolve after the next frame paints (after `applyChildren`), so a
    // consumer that just mutated layout — `setImageVisible`, `setImageSrc`,
    // a manual DOM tweak — can `await` measurable rects. (`setSources`
    // already resolves on this gate; this covers the in-between mutations.)
    function whenLayoutSettled() {
      return new Promise(function(resolve) {
        var u = null, t = null;
        function fin() {
          if (u) { try { u(); } catch (_) {} }
          if (t) clearTimeout(t);
          resolve();
        }
        engine.requestFrame();
        u = engine.bus.on("update-viewport", fin);
        t = setTimeout(fin, 1000);
      });
    }

    // Tear down on the canvas's destroyed lifecycle — flush a final
    // view-blur with reason "destroyed" so consumers can persist a
    // pending duration. Wrap engine.teardown so consumers don't have
    // to remember a second cleanup call.
    var originalTeardown = engine.teardown;
    var teardownWithTracker = function() {
      if (viewTracker.isEnabled()) viewTracker.disable("destroyed");
      originalTeardown();
    };

    return {
      el: el,
      setSources: setSources,
      setImageSrc: setImageSrc,
      whenLayoutSettled: whenLayoutSettled,
      stage: stage,
      imgs: imgs,
      navEl: engine.navEl,
      bus: engine.bus,
      getTransform: engine.getTransform,
      getViewportSize: engine.getViewportSize,
      isInfiniteCanvas: engine.isInfiniteCanvas,
      getCanvasSize: function() { return { width: canvasW, height: canvasH }; },
      getImages: getImages,
      imageBoundsFor: imageBoundsFor,
      getExtension: getExtension,
      fit: engine.fit,
      zoomAt: engine.zoomAt,
      panBy: engine.panBy,
      setTransform: engine.setTransform,
      setZoomFloor: engine.setZoomFloor,
      getZoomFloor: engine.getZoomFloor,
      getPanCentre: engine.getPanCentre,
      setZoomCeiling: engine.setZoomCeiling,
      setPanLocked: engine.setPanLocked,
      setTwoFingerPanInverted: engine.setTwoFingerPanInverted,
      getTwoFingerPanInverted: engine.getTwoFingerPanInverted,
      setPanBounds: engine.setPanBounds,
      setHomeAction: engine.setHomeAction,
      suppressNextTap: engine.suppressNextTap,
      // 0.5.7+ rotation API. Without these re-exports, the canvas
      // handle's `handle.setRotation(...)` proxy at the next layer
      // up throws `TypeError: controller.setRotation is not a function`
      // even though the engine has the method.
      setRotation: engine.setRotation,
      getRotation: engine.getRotation,
      rotateBy: engine.rotateBy,
      setGridVisible: engine.setGridVisible,
      getGridVisible: engine.getGridVisible,
      // 0.5.7+ programmatic nav-button equivalents. Same leak
      // pattern — the engine has them, the handle proxies through,
      // the controller layer in between had to re-export.
      zoomIn: engine.zoomIn,
      zoomOut: engine.zoomOut,
      toggleFullscreen: engine.toggleFullscreen,
      requestHome: engine.requestHome,
      getZoomPercent: engine.getZoomPercent,
      isAtHome: engine.isAtHome,
      zoomToActualSize: engine.zoomToActualSize,
      toggleActualSize: engine.toggleActualSize,
      fitToView: engine.fitToView,
      setImageVisible: setImageVisible,
      getHiddenImageIds: getHiddenImageIds,
      setMemoryWindow: setMemoryWindow,
      enableViewTracking: function(o) { viewTracker.enable(o || {}); },
      disableViewTracking: function() { viewTracker.disable("disabled"); },
      getFocusedImage: function() { return viewTracker.getFocused(); },
      refreshLayout: refreshLayout,
      teardown: teardownWithTracker
    };
  }

  // ===========================================================================
  // Canvas handle — public surface via window.Fresco.viewerFor(id) /
  // window.Fresco.onReady(id, cb).
  // ===========================================================================

  function makeCanvasHandle(controller) {
    var bus = controller.bus;
    var el = controller.el;

    // image-pixel → screen-pixel under composite transform
    //   screen = rect.{left,top} + translate(tx, ty) · rotate(rot) · scale(s) · image
    // For 90°-snapped rotations the cos/sin pair is exact (see
    // `rotationCosSin`) so the formula reduces to swaps + negations
    // — no trig at the hot path.
    function imageToScreen(pt) {
      var t = controller.getTransform();
      var rect = el.getBoundingClientRect();
      var cs = rotationCosSin(t.rotation || 0);
      var px = (pt.x || 0) * t.s;
      var py = (pt.y || 0) * t.s;
      return {
        x: rect.left + t.tx + px * cs.c - py * cs.sn,
        y: rect.top  + t.ty + px * cs.sn + py * cs.c
      };
    }

    // Inverse of imageToScreen — subtract translate, apply inverse
    // rotation (transpose of the rotation matrix), then divide by
    // scale to get image-pixel coords.
    function screenToImage(pt) {
      var t = controller.getTransform();
      var rect = el.getBoundingClientRect();
      var cs = rotationCosSin(t.rotation || 0);
      var dx = (pt.x || 0) - rect.left - t.tx;
      var dy = (pt.y || 0) - rect.top  - t.ty;
      return {
        x: ( dx * cs.c + dy * cs.sn) / t.s,
        y: (-dx * cs.sn + dy * cs.c) / t.s
      };
    }

    function getViewportBounds() {
      var t = controller.getTransform();
      var v = controller.getViewportSize();
      return {
        x: -t.tx / t.s,
        y: -t.ty / t.s,
        width: v.vw / t.s,
        height: v.vh / t.s
      };
    }

    function fitBounds(rect, opts) {
      if (!rect || rect.width <= 0 || rect.height <= 0) return;
      var v = controller.getViewportSize();
      var t = controller.getTransform();
      var rot = t.rotation || 0;
      // The rect is in unrotated canvas-px (the consumer's natural
      // frame of reference). After rotation, its screen-space bbox
      // has different dims — for 90° / 270° the width and height
      // swap. Compute newS against the rotated bbox so the rotated
      // content actually fits the viewport.
      var fitW = (rot === 90 || rot === 270) ? rect.height : rect.width;
      var fitH = (rot === 90 || rot === 270) ? rect.width  : rect.height;
      var newS = Math.min(v.vw / fitW, v.vh / fitH);
      // Compute the rotated + scaled bbox of `rect` in stage-local
      // coords (pre-translate). The same math as `rotatedRectBBox`
      // but inlined since the engine's helper isn't exposed.
      var cs = rotationCosSin(rot);
      var x1 = rect.x, y1 = rect.y;
      var x2 = rect.x + rect.width, y2 = rect.y + rect.height;
      var pts = [[x1, y1], [x2, y1], [x2, y2], [x1, y2]];
      var minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (var i = 0; i < 4; i++) {
        var px = pts[i][0] * newS * cs.c - pts[i][1] * newS * cs.sn;
        var py = pts[i][0] * newS * cs.sn + pts[i][1] * newS * cs.c;
        if (px < minX) minX = px;
        if (px > maxX) maxX = px;
        if (py < minY) minY = py;
        if (py > maxY) maxY = py;
      }
      // Center the rotated bbox in the viewport.
      var newTx = (v.vw - (maxX - minX)) / 2 - minX;
      var newTy = (v.vh - (maxY - minY)) / 2 - minY;
      if (opts && opts.animate) {
        controller.animateTo(newTx, newTy, newS, {
          duration: opts.duration,
          easing: opts.easing
        });
      } else {
        controller.setTransform(newTx, newTy, newS);
      }
    }

    function fitImage(id) {
      var bounds = controller.imageBoundsFor(id);
      if (bounds) fitBounds(bounds);
    }

    return {
      container: el,
      imageToScreen: imageToScreen,
      screenToImage: screenToImage,
      getViewportBounds: getViewportBounds,
      fitBounds: fitBounds,
      getCanvasSize: controller.getCanvasSize,
      getImages: controller.getImages,
      imageBoundsFor: controller.imageBoundsFor,
      fitImage: fitImage,
      // Replace the whole image set in place (no DOM remount) — page-session
      // state (Pointer Lock, media pipelines, peer overlays bound via
      // `handle.on(...)`) survives. `sources` is an array of
      // `{ src, x?, y?, width?, height?, id?, z_index? }`; `opts` accepts
      // `reset_view` (default true → fit-to-canvas after the swap), an
      // optional `extensions` map (atomically replaces canvas-level
      // extensions, e.g. `extensions.etcher.annotations`), and optional
      // `canvasWidth`/`canvasHeight` (else the new images' bounding box).
      // Returns a Promise that resolves once the first new frame is
      // decodable; rejects on empty/malformed input. Fires `open` +
      // `sources-changed` so overlays rebuild.
      setSources: controller.setSources,
      // Swap one image's `src` in place (load-window paging) without a full
      // relayout; re-latches load tracking so `image-loaded` fires.
      setImageSrc: controller.setImageSrc,
      // Promise that resolves after the next frame paints — `await` it to
      // measure rects after a `setImageVisible` / `setImageSrc` mutation.
      whenLayoutSettled: controller.whenLayoutSettled,
      // Per-image visibility on a multi-image canvas. Hidden images
      // stay in layout (pan-bounds + fit math are anchored) but
      // their <img> is `display: none`; the `image-visibility-change`
      // event lets extensions pinned to a specific image hide / re-
      // show in lockstep. `getHiddenImageIds` returns a snapshot for
      // late-mounting extensions that need to seed their initial state.
      setImageVisible: controller.setImageVisible,
      getHiddenImageIds: controller.getHiddenImageIds,
      getExtension: controller.getExtension,
      // Opt-in zoom + pan constraint controls (0.5.1+). Same semantics
      // as on the viewer handle — paged readers / wallpaper croppers
      // use these to fix the zoom-out floor and lock pan at fit. All
      // three default to no-op so consumers who don't opt in see
      // identical pre-0.5.1 behavior.
      setZoomFloor:   function(v) { controller.setZoomFloor(v); },
      getZoomFloor:   function() { return controller.getZoomFloor(); },
      setZoomCeiling: function(v) { controller.setZoomCeiling(v); },
      setPanLocked:   function(b) { controller.setPanLocked(b); },
      setTwoFingerPanInverted: function(b) { controller.setTwoFingerPanInverted(b); },
      getTwoFingerPanInverted: function() { return controller.getTwoFingerPanInverted(); },
      // Per-region pan clamp + custom home action (both 0.5.2+).
      // Paged readers narrow `setPanBounds` to the current page rect
      // so dragging never wanders into adjacent pages, and override
      // `setHomeAction` so the nav-column reset button (+ the `0`
      // keyboard shortcut) fits the active page instead of the
      // whole multi-image canvas. Both were on the controller from
      // the start but the canvas handle's surface never re-exported
      // them — a leak fixed here.
      setPanBounds:   function(b) { controller.setPanBounds(b); },
      getPanCentre:   function() { return controller.getPanCentre ? controller.getPanCentre() : "content"; },
      // Forwarded for the peers that manage pan bounds (Etcher's
      // outside-the-picture ink): they must know whether a clamp exists
      // to loosen, and they treat a handle that cannot answer as
      // untouchable — omitting this silently disabled the feature on
      // every surface served by THIS handle while the inner handles
      // worked, the same trap zoomAt fell into once.
      isInfiniteCanvas: function() { return controller.isInfiniteCanvas(); },
      setHomeAction:  function(fn) { controller.setHomeAction(fn); },
      // Suppress the next `tap` event for `ms` (default 250) —
      // peer libraries committing a gesture that races the OS-
      // synthesized tap pipeline call this to swallow it. See
      // `[data-fresco-suppress-tap]` for the static-element
      // counterpart that doesn't require a function call.
      suppressNextTap: function(ms) { controller.suppressNextTap(ms); },
      // Live transform getter for consumers building layered
      // overlays (annotation surfaces, diagnostic HUDs) that need
      // to mirror the canvas's `{tx, ty, s, rotation}` between
      // frames. The `rotation` field is in degrees, snapped to one
      // of {0, 90, 180, 270}.
      getTransform:   function() { return controller.getTransform(); },
      // 90°-snapped content rotation (0.5.7+). `setRotation(deg)`
      // re-fits + emits a `rotate` event when the snapped value
      // actually changes; `getRotation()` returns the current angle;
      // `rotateBy(delta)` is sugar for `setRotation(getRotation()+delta)`
      // — wire it to a toggle button. The host element + nav
      // overlay stay un-rotated; only the stage (and everything
      // inside it, including extension SVG overlays) rotates.
      setGridVisible: function(on) { controller.setGridVisible(on); },
      getGridVisible: function() { return controller.getGridVisible(); },
      setRotation:    function(deg) { controller.setRotation(deg); },
      getRotation:    function() { return controller.getRotation(); },
      rotateBy:       function(delta) { controller.rotateBy(delta); },
      // Named quarter-turn helpers (right = +90 CW, left = -90 CCW), the
      // twins of the two rotate nav buttons. Both fire the `rotate` event,
      // so they persist exactly like the buttons when persist_rotation is on.
      rotateRight:    function() { controller.rotateBy(90); },
      rotateLeft:     function() { controller.rotateBy(-90); },
      // Programmatic equivalents of the built-in nav buttons —
      // identical step factors + anchors. Consumers hiding the
      // chrome (`:nav_buttons={[]}`) but wanting the same actions
      // wired to their own buttons / shortcuts call these.
      zoomIn:           function(f) { controller.zoomIn(f); },
      zoomOut:          function(f) { controller.zoomOut(f); },
      toggleFullscreen: function()  { controller.toggleFullscreen(); },
      requestHome:      function()  { controller.requestHome(); },
      // Zoom in real pixels (`actual_size`): 100 = one natural pixel per
      // physical screen pixel; the button's to-100%-and-back; fit whatever
      // home is.
      getZoomPercent:   function()  { return controller.getZoomPercent(); },
      isAtHome:         function()  { return controller.isAtHome ? controller.isAtHome() : true; },
      zoomToActualSize: function()  { controller.zoomToActualSize(); },
      toggleActualSize: function()  { controller.toggleActualSize(); },
      fitToView:        function()  { controller.fitToView(); },
      // 0.5.2+ view-tracking — emits "view-focus" / "view-blur" on
      // the bus when the dominant image changes. Default off; enable
      // explicitly to start.
      enableViewTracking:  function(o) { controller.enableViewTracking(o || {}); },
      disableViewTracking: function() { controller.disableViewTracking(); },
      getFocusedImage:     function() { return controller.getFocusedImage(); },
      on: bus.on,
      _emit: bus._emit,
      appendNavButton: function(svg, title, onClick, opts) {
        return attachNavButton(controller.navEl, svg, title, onClick, opts);
      }
    };
  }

  // ===========================================================================
  // FrescoCanvas LiveView hook
  // ===========================================================================

  window.FrescoHooks.FrescoCanvas = {
    mounted: function() {
      injectStyles();
      var controller = mountFrescoCanvas(this.el);
      if (!controller) return;
      this.controller = controller;
      var handle = makeCanvasHandle(controller);
      this.handle = handle;
      // Initialize layout rev so `updated` can fast-path extension-only churn.
      this._layoutRev = this.el.dataset.canvasWidth + "x" + this.el.dataset.canvasHeight + ":" +
                        this.el.querySelectorAll("[data-fresco-canvas-img]").length;
      publishReady(this.el.id, handle);
      this._rotateOff = wireRotationBridge(this);
    },

    updated: function() {
      if (!this.controller) return;
      // Fast-path: skip DOM work when only `data-extensions` changed (Etcher
      // will churn this on every annotation edit). Re-layout only when canvas
      // dims or image count actually changed.
      var nextLayoutRev = this.el.dataset.canvasWidth + "x" + this.el.dataset.canvasHeight + ":" +
                         this.el.querySelectorAll("[data-fresco-canvas-img]").length;
      if (nextLayoutRev !== this._layoutRev) {
        this._layoutRev = nextLayoutRev;
        this.controller.refreshLayout();
      }
    },

    destroyed: function() {
      if (this._rotateOff) { try { this._rotateOff(); } catch (_) {} this._rotateOff = null; }
      if (this.el && this.el.id) unpublish(this.el.id);
      if (this.controller) {
        try { this.controller.teardown(); } catch (_) {}
        this.controller = null;
      }
      this.handle = null;
    }
  };
})();
